// Y2K streetwear pixel chibi: layered sprite builder.
//
// Everything is drawn into a 40x56 pixel grid with the feet at the bottom center, then
// scaled up with nearest-neighbour sampling. Each body part and cosmetic is its own
// layer with an auto outline, so any top / bottom / shoes / hat / glasses / headphones
// combine on any body.

import { Layer, T, bounds, composite, crop, flipX, hex, mix, ramp, type Bitmap, type RGB } from "./pixel";

export const SW = 40;
export const SH = 56;
const CX = 20; // body center: the split between columns 19 and 20

// Vertical anchors (rows)
const HEAD_TOP = 8;
const HEAD_HW = [6, 8, 10, 11, 12, 12, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 12, 12, 11, 9, 7, 4];
const CHIN_Y = HEAD_TOP + HEAD_HW.length - 1; // 30
const EYE_Y = HEAD_TOP + 13; // 21
const SHOULDER_Y = CHIN_Y + 2; // 32
const WAIST_Y = 41;
const HIP_Y = 42;
const LEG_Y = 44;
const HEM_Y = 51;
const SHOE_Y = 52; // shoes occupy 52..55
export const FOOT_Y = 55;

export interface Part {
  kind: string;
  color: string;
  accent?: string; // trim / print color
  accent2?: string;
}

export interface ChibiLook {
  skin: number;
  hairColor: string;
  hairStyle: string;
  eyeColor: string;
  top: Part;
  bottom: Part;
  shoes: Part;
  hat?: Part | null;
  face?: Part | null; // glasses
  ears?: Part | null; // headphones, chains
}

export interface Frame {
  back: boolean; // facing away from the camera
  step: -1 | 0 | 1; // which leg is lifted while walking
  arms: "down" | "wave" | "up";
  wave?: 0 | 1; // waving hand frame
  bob?: number; // whole-body vertical offset in pixels (negative = up)
}

// Skin ramps are hand-picked: generic ramps make skin look grey.
const SKINS = [
  { hi: "#fff6f0", base: "#ffe6d8", sh: "#f6c8b6", deep: "#e3a596", line: "#8a4e4a", blush: "#ffb8c6" },
  { hi: "#fff1e2", base: "#fddabb", sh: "#efb994", deep: "#d29472", line: "#7d4632", blush: "#ffa9a2" },
  { hi: "#fbe4c8", base: "#efc59d", sh: "#d9a277", deep: "#bb7f58", line: "#6b3b25", blush: "#f2948a" },
  { hi: "#eec59e", base: "#d4a273", sh: "#b78256", deep: "#95633e", line: "#57301b", blush: "#e0806e" },
  { hi: "#cb986c", base: "#a97248", sh: "#8c5837", deep: "#6e4227", line: "#3d2112", blush: "#c4664f" },
  { hi: "#9c6c49", base: "#7c4f32", sh: "#633c24", deep: "#4c2c19", line: "#29150b", blush: "#a3533d" },
];
export const SKIN_COUNT = SKINS.length;

function skinPal(i: number): RGB[] {
  const s = SKINS[i] ?? SKINS[1];
  const p: RGB[] = [];
  p[T.HI] = hex(s.hi);
  p[T.BASE] = hex(s.base);
  p[T.SH] = hex(s.sh);
  p[T.DEEP] = hex(s.deep);
  p[T.INK] = hex(s.line);
  p[T.A] = hex(s.blush);
  p[T.W] = [255, 255, 255];
  return p;
}
const skinLine = (i: number) => hex((SKINS[i] ?? SKINS[1]).line);

const newLayer = (pal: RGB[], outline?: RGB | null) => new Layer(SW, SH, pal, outline);

/** Thick line: stamps a w x w square along the segment. */
function brush(L: Layer, x0: number, y0: number, x1: number, y1: number, w: number, v: number) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  const o = Math.floor(w / 2);
  for (let i = 0; i <= n; i++) {
    const x = Math.round(x0 + ((x1 - x0) * i) / n), y = Math.round(y0 + ((y1 - y0) * i) / n);
    L.rect(x - o, y - o, w, w, v);
  }
}

// ---------------------------------------------------------------- hair styles

interface HairStyle {
  /** zig-zag bottom edge of the bangs: [x, y] points, linearly interpolated */
  bangs: [number, number][];
  /** bottom row of the side locks in front of the ears */
  side: number;
  /** extra width outside the head for volume */
  puff: number;
  /** long hair behind the body: bottom row, or 0 */
  back: number;
  /** tufts sticking out of the silhouette: [x, y] tips */
  tufts?: [number, number][];
  braids?: boolean;
  wavy?: boolean;
  /** rope texture on the tufts and bangs */
  twists?: boolean;
  /** slicked back into a high bun */
  bun?: boolean;
  /** a crisp middle part */
  part?: boolean;
  /** chunky blonde streaks framing the face */
  streaks?: boolean;
  /** two little buns up on the crown */
  spaceBuns?: boolean;
}

const HAIR: Record<string, HairStyle> = {
  // messy fluffy idol cut
  fluffy: {
    bangs: [[7, 21], [9, 19], [11, 23], [13, 18], [15, 22], [17, 17], [19, 21], [21, 17], [23, 21], [25, 18], [27, 22], [29, 18], [31, 23], [33, 21]],
    side: 25, puff: 2, back: 0,
    tufts: [[3, 15], [9, 3], [16, 1], [24, 1], [34, 13]],
  },
  // blunt bob with straight bangs
  bob: {
    bangs: [[7, 18], [8, 19], [12, 19], [13, 20], [14, 19], [25, 19], [26, 20], [27, 19], [31, 19], [32, 18]],
    side: 31, puff: 2, back: 0,
  },
  // long straight hair, hime-cut bangs
  long: {
    bangs: [[7, 18], [8, 19], [31, 19], [32, 18]],
    side: 34, puff: 2, back: 50,
  },
  // long wavy hair with a side-swept fringe
  wavy: {
    bangs: [[7, 25], [9, 20], [12, 17], [16, 18], [19, 21], [21, 16], [24, 17], [28, 19], [31, 21], [33, 26]],
    side: 32, puff: 3, back: 49, wavy: true,
  },
  // twin braids
  braids: {
    bangs: [[7, 21], [9, 19], [12, 21], [15, 18], [18, 21], [21, 18], [24, 21], [27, 18], [30, 21], [32, 20]],
    side: 26, puff: 1, back: 0, braids: true,
  },
  // short spiky with an undercut
  spiky: {
    bangs: [[7, 17], [10, 20], [12, 16], [15, 19], [18, 15], [21, 19], [24, 14], [27, 18], [30, 16], [33, 18]],
    side: 21, puff: 0, back: 0,
    tufts: [[4, 12], [6, 4], [11, 0], [17, 0], [23, 0], [29, 1], [34, 4], [36, 11]],
  },
  // 90s curtains: middle part, fringe swept to both sides
  curtains: {
    bangs: [[7, 25], [9, 23], [12, 20], [15, 17], [18, 14], [19, 13], [20, 13], [21, 14], [24, 17], [27, 20], [30, 23], [32, 25]],
    side: 27, puff: 1, back: 0,
  },
  // shaggy wolf cut: choppy fringe, layered sides down past the jaw
  wolf: {
    bangs: [[7, 22], [9, 19], [11, 22], [13, 18], [16, 21], [18, 17], [20, 20], [22, 17], [25, 21], [27, 18], [29, 22], [31, 19], [33, 22]],
    side: 35, puff: 2, back: 38,
    tufts: [[7, 4], [14, 0], [25, 1], [33, 5]],
  },
  // tight buzz cut with a crisp line-up
  buzz: {
    bangs: [[7, 16], [9, 14], [12, 13], [27, 13], [30, 14], [32, 16]],
    side: 19, puff: -1, back: 0,
  },
  // short twists standing up all over, ends falling over the forehead
  twists: {
    bangs: [[7, 19], [9, 22], [11, 18], [13, 22], [15, 18], [17, 22], [19, 18], [21, 22], [23, 18], [25, 22], [27, 18], [29, 22], [31, 18], [33, 21]],
    side: 24, puff: 1, back: 0, twists: true,
    tufts: [[2, 11], [4, 4], [8, 0], [13, 0], [18, 0], [23, 0], [28, 0], [33, 3], [37, 10]],
  },
  // slicked back off the face into a high bun
  bun: {
    bangs: [[7, 18], [9, 15], [12, 13], [16, 12], [23, 12], [27, 13], [30, 15], [32, 18]],
    side: 20, puff: -1, back: 0, bun: true,
  },
  // long, glossy and dead straight, with a middle part
  sleek: {
    bangs: [[7, 27], [9, 22], [12, 17], [15, 14], [18, 12], [19, 11], [20, 11], [21, 12], [24, 14], [27, 17], [30, 22], [32, 27]],
    side: 36, puff: 1, back: 50, part: true,
  },
  // long, layered and flipped out, with chunky streaks either side of the face
  highlights: {
    bangs: [[7, 25], [9, 21], [12, 18], [15, 16], [18, 15], [19, 14], [20, 14], [21, 15], [24, 16], [27, 18], [30, 21], [32, 25]],
    side: 35, puff: 2, back: 47, streaks: true,
  },
  // two small buns on the crown, a choppy fringe and loose pieces at the sides
  spacebuns: {
    bangs: [[7, 20], [9, 18], [12, 20], [15, 17], [18, 20], [21, 17], [24, 20], [27, 17], [30, 20], [32, 19]],
    side: 28, puff: 0, back: 0, spaceBuns: true,
  },
};
export const HAIR_STYLE_IDS = Object.keys(HAIR);

function edgeAt(pts: [number, number][], x: number): number {
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1], [x1, y1] = pts[i];
    if (x <= x1) return Math.round(y0 + ((y1 - y0) * (x - x0)) / (x1 - x0));
  }
  return pts[pts.length - 1][1];
}

function hairPal(color: string): RGB[] {
  const p = ramp(color);
  p[T.A] = mix(hex(color), [255, 255, 255], 0.55); // shine
  p[T.B] = mix(hex(color), [250, 232, 168], 0.7); // highlight streaks
  return p;
}

// ---------------------------------------------------------------- head & face

function head(skin: number, eyeColor: string, hairStyle: string | null, back: boolean): Layer {
  const pal = skinPal(skin);
  const eye = ramp(eyeColor);
  pal[T.B] = eye[T.DEEP];
  pal[T.C] = eye[T.BASE];
  pal[T.B] = mix(eye[T.DEEP], eye[T.BASE], 0.3);
  pal[10] = mix(eye[T.HI], [255, 255, 255], 0.2);
  pal[11] = [34, 22, 36]; // lashes
  pal[12] = mix(eye[T.DEEP], [20, 10, 30], 0.5); // pupil
  const L = newLayer(pal, skinLine(skin));
  HEAD_HW.forEach((hw, j) => L.hline(CX - hw, CX + hw - 1, HEAD_TOP + j, T.BASE));
  // ears
  L.rect(CX - 14, EYE_Y - 1, 1, 4).rect(CX + 13, EYE_Y - 1, 1, 4);
  L.set(CX - 14, EYE_Y + 1, T.SH).set(CX + 13, EYE_Y + 1, T.SH);
  // neck
  L.rect(CX - 2, CHIN_Y + 1, 4, 1, T.SH);
  // soft shading along the right cheek and under the chin
  for (let j = 0; j < HEAD_HW.length; j++) {
    const y = HEAD_TOP + j, hw = HEAD_HW[j];
    L.set(CX + hw - 1, y, T.SH);
    if (j >= 15) L.set(CX + hw - 2, y, T.SH);
  }
  if (back) return L;
  // shadow the bangs cast on the forehead
  const st = hairStyle ? HAIR[hairStyle] : null;
  if (st) for (let x = CX - 12; x < CX + 12; x++) L.set(x, edgeAt(st.bangs, x) + 1, T.SH);
  // big glossy eyes: heavy upper lash, gradient iris, sparkle
  const key = { K: 11, a: T.B, b: T.C, c: 10, w: T.W, p: 12, k: T.SH };
  const eyeRows = [
    "..KKKKK",
    "KKKKKKK",
    ".Kppppa",
    ".Kwwpaa",
    ".Kwapab",
    ".aabbbb",
    "..bccb.",
  ];
  L.stamp(10, EYE_Y - 1, eyeRows, key);
  L.stamp(23, EYE_Y - 1, eyeRows, key, true);
  // blush, nose, mouth
  L.hline(10, 12, EYE_Y + 6, T.A).hline(27, 29, EYE_Y + 6, T.A);
  L.set(20, EYE_Y + 6, T.SH);
  L.hline(19, 20, EYE_Y + 8, T.DEEP);
  return L;
}

/** Long hair that hangs behind the body (front view). */
function hairBack(color: string, styleId: string): Layer | null {
  const st = HAIR[styleId];
  if (!st?.back) return null;
  const L = newLayer(hairPal(color));
  const top = HEAD_TOP + 4, bottom = st.back;
  for (let y = top; y <= bottom; y++) {
    // hugs the head, flares a little over the shoulders, then narrows toward the tips
    const t = (y - CHIN_Y) / (bottom - CHIN_Y);
    let hw = y < CHIN_Y ? 14 + st.puff : Math.round(14 + st.puff + Math.sin(Math.min(1, t * 1.6) * Math.PI) * 1.5 - Math.max(0, t - 0.6) * 5);
    if (st.wavy) hw += Math.round(Math.sin(y * 0.6) * 1.3);
    L.hline(CX - hw, CX + hw - 1, y, T.BASE);
  }
  // pointed strand tips along the bottom
  for (let x = CX - 16; x < CX + 16; x += 3) {
    L.set(x, bottom + 1, T.BASE).set(x + 1, bottom + 1, T.BASE).set(x + 1, bottom + 2, T.BASE);
  }
  L.shade({ right: 2, bottom: 1, hi: false });
  for (let x = CX - 14; x < CX + 14; x += 3) for (let y = CHIN_Y; y < bottom; y++) if (L.get(x, y) === T.BASE && (y + x) % 7 < 5) L.set(x, y, T.SH);
  if (st.streaks) streaks(L, [CX - 12, CX + 9]);
  return L;
}

/** Chunky highlights: a three-pixel streak running down from the crown at each given column, with a darker edge. */
function streaks(L: Layer, cols: number[]) {
  for (const sx of cols) for (let y = 0; y < SH; y++) for (const x of [sx, sx + 1, sx + 2]) {
    const v = L.get(x, y);
    if (v === T.BASE || v === T.HI || v === T.A) L.set(x, y, T.B);
  }
}

/** Main hair mass: crown, bangs, side locks. In back view it covers the whole head. */
function hairFront(color: string, styleId: string, back: boolean, hatted = false): Layer | null {
  const st = HAIR[styleId];
  if (!st) return null;
  const L = newLayer(hairPal(color));
  const p = st.puff;
  // rounded dome following the skull, a bit bigger
  const dome = [4, 7, 9, 11, 12, 13, 13, 14, 14, 14, 14, 14, 14, 14, 14, 14];
  dome.forEach((hw, j) => L.hline(CX - hw - p, CX + hw + p - 1, HEAD_TOP - 2 + j, T.BASE));
  if (st.bun && !hatted) L.ellipse(CX - 5, HEAD_TOP - 10, CX + 4, HEAD_TOP - 3, T.BASE);
  if (st.spaceBuns && !hatted) L.ellipse(CX - 13, HEAD_TOP - 6, CX - 6, HEAD_TOP + 1, T.BASE).ellipse(CX + 5, HEAD_TOP - 6, CX + 12, HEAD_TOP + 1, T.BASE);
  // tufts
  for (const [tx, ty] of hatted ? [] : st.tufts ?? []) {
    const bx = tx < CX - 3 ? tx + 4 : tx > CX + 3 ? tx - 4 : tx;
    brush(L, tx, ty, bx, HEAD_TOP + 2, 2, T.BASE);
    brush(L, tx + (tx < CX ? 1 : 0), ty + 2, bx, HEAD_TOP + 4, 3, T.BASE);
  }
  if (back) {
    for (let j = 10; j < HEAD_HW.length; j++) {
      const hw = HEAD_HW[j] + p + (j < 18 ? 1 : 0);
      L.hline(CX - hw, CX + hw - 1, HEAD_TOP + j, T.BASE);
    }
    if (st.side > CHIN_Y && !(st.back && st.back < 44)) for (let y = CHIN_Y; y <= st.side; y++) L.hline(CX - 13 - p, CX + 12 + p, y, T.BASE);
    if (st.back) {
      const shag = st.back < 44, y0 = shag ? HEAD_TOP + 14 : CHIN_Y;
      for (let y = y0; y <= st.back; y++) {
        let hw = 14 + p;
        if (st.wavy) hw += Math.round(Math.sin(y * 0.6) * 1.3);
        if (shag) hw -= Math.round(Math.max(0, (y - CHIN_Y) / (st.back - CHIN_Y)) * 5); // short shag tapers into the neck
        L.hline(CX - hw, CX + hw - 1, y, T.BASE);
      }
      const tw = st.back < 44 ? 10 : 15;
      for (let x = CX - tw; x < CX + tw; x += 3) L.set(x, st.back + 1, T.BASE).set(x + 1, st.back + 1, T.BASE).set(x + 1, st.back + 2, T.BASE);
    }
  } else {
    // bangs: cut the face out under the zig-zag edge
    for (let x = CX - 12; x < CX + 12; x++) for (let y = edgeAt(st.bangs, x) + 1; y < SH; y++) L.set(x, y, 0);
    // side locks with pointed tips
    const sw = 3 + p;
    for (let y = HEAD_TOP + 8; y <= st.side; y++) {
      const wv = st.wavy ? Math.round(Math.sin(y * 0.7)) : 0;
      const tip = st.side - y; // 0 at the very bottom
      const lw = tip === 0 ? 1 : tip === 1 ? 2 : sw;
      L.hline(CX - 13 - p + wv, CX - 14 - p + wv + lw, y, T.BASE);
      L.hline(CX + 13 + p - lw + wv, CX + 12 + p + wv, y, T.BASE);
    }
  }
  L.shade({ right: 2, bottom: 1, hi: false });
  if (!back) {
    for (let x = CX - 12; x < CX + 12; x++) {
      const e = edgeAt(st.bangs, x), l = edgeAt(st.bangs, x - 1), r = edgeAt(st.bangs, x + 1);
      // notch: a short dark crease going up
      if (e < l && e <= r) for (let k = 0; k < 4; k++) if (L.get(x, e - k)) L.set(x, e - k, k < 2 ? T.DEEP : T.SH);
      // tip: a long strand line curving up toward the crown
      if (e > l && e >= r) {
        const tx = x + (x < CX ? 1 : -1);
        for (let k = 2; k < 9; k++) {
          const yy = e - k, xx = Math.round(tx + ((CX - tx) * k) / 22);
          if (L.get(xx, yy) === T.BASE) L.set(xx, yy, T.SH);
        }
      }
    }
    for (const sx of [CX - 12 - p, CX + 11 + p]) for (let y = HEAD_TOP + 12; y < st.side - 1; y++) if (L.get(sx, y)) L.set(sx, y, T.SH);
    // slicked hair: comb lines running from the hairline back over the crown
    if (st.bun) for (let x = CX - 11; x < CX + 11; x += 3) for (let y = HEAD_TOP - 1; y < edgeAt(st.bangs, x) - 1; y++) if (L.get(x, y) === T.BASE) L.set(x, y, T.SH);
    if (st.part) L.vline(CX - 1, HEAD_TOP - 2, edgeAt(st.bangs, CX - 1), T.DEEP);
  } else {
    for (let x = CX - 11; x < CX + 11; x += 4) for (let y = HEAD_TOP + 8; y < Math.max(CHIN_Y, st.side) - 1; y++) if (L.get(x, y)) L.set(x, y, T.SH);
  }
  // darker roots along the bottom of the dome, lighter crown
  for (let y = HEAD_TOP - 4; y < HEAD_TOP + 3; y++) for (let x = 0; x < SW; x++) if (L.get(x, y) === T.BASE && L.get(x - 1, y) && L.get(x, y - 1) && x < CX + 4) L.set(x, y, T.HI);
  // "angel ring" shine: short dashes in an arc on the upper left
  for (let x = CX - 11; x <= CX + 3; x++) {
    const y = HEAD_TOP + 3 + Math.round(((x - CX + 4) * (x - CX + 4)) / 30);
    if (!L.get(x, y) || (x + 1) % 4 === 0) continue;
    L.set(x, y, T.A);
    if (L.get(x - 1, y - 1) && (x % 4) === 1) L.set(x - 1, y - 1, T.A);
  }
  // bun: wrapped strands and a tie at the base
  if (st.bun && !hatted) {
    L.hline(CX - 3, CX + 2, HEAD_TOP - 3, T.DEEP);
    for (const [x, y] of [[CX - 3, HEAD_TOP - 8], [CX - 2, HEAD_TOP - 7], [CX + 1, HEAD_TOP - 6], [CX + 2, HEAD_TOP - 5]]) L.set(x, y, T.SH);
  }
  // space buns: a wrapped swirl on each and a tie at the base
  if (st.spaceBuns && !hatted) for (const bx of [CX - 13, CX + 5]) {
    for (const [x, y] of [[1, -4], [2, -3], [5, -3], [6, -2]]) L.set(bx + x, HEAD_TOP + y, T.SH);
    L.hline(bx + 2, bx + 5, HEAD_TOP + 1, T.DEEP);
  }
  // chunky highlights: two-pixel blonde stripes, framing the face in front and running down the back
  if (st.streaks) streaks(L, back ? [CX - 9, CX + 6] : [CX - 12, CX + 9]);
  // twists: a dark crease every other row down each strand reads as rope
  if (st.twists) for (let y = 0; y < HEAD_TOP + 4; y++) for (let x = 0; x < SW; x++) if (L.get(x, y) === T.BASE && (x + y) % 3 === 0) L.set(x, y, T.SH);
  // braids hang at both sides, so they show from the front and from behind
  if (st.braids) {
    for (const bx of [CX - 16, CX + 13]) {
      for (let y = 26, i = 0; y <= 45; y += 3, i++) {
        // interlocking lobes, alternating sides
        const o = i % 2 ? 1 : 0;
        L.rect(bx - o, y, 3, 2, T.BASE).set(bx + 2 - o, y, T.HI);
        L.rect(bx + o, y + 2, 3, 1, T.SH);
      }
      L.rect(bx, 47, 3, 1, T.A).rect(bx - 1, 48, 5, 2, T.BASE).set(bx + 1, 50, T.BASE);
    }
  }
  return L;
}

// ---------------------------------------------------------------- arms

interface Arm { sx: number; sy: number; ex: number; ey: number; up: boolean; side: -1 | 1 }

function arms(f: Frame): Arm[] {
  const s = f.step;
  const left: Arm = { sx: 13, sy: SHOULDER_Y + 1, ex: 12, ey: SHOULDER_Y + 7 - s, up: false, side: -1 };
  let right: Arm = { sx: 26, sy: SHOULDER_Y + 1, ex: 27, ey: SHOULDER_Y + 7 + s, up: false, side: 1 };
  if (f.arms !== "down") right = { sx: 26, sy: SHOULDER_Y + 1, ex: 34 + (f.wave ? 1 : 0), ey: SHOULDER_Y - 6 - (f.wave ? 1 : 0), up: true, side: 1 };
  const l = f.arms === "up" ? { sx: 13, sy: SHOULDER_Y + 1, ex: 5 - (f.wave ? 1 : 0), ey: SHOULDER_Y - 6 - (f.wave ? 0 : 1), up: true, side: -1 as const } : left;
  return [l, right];
}

function armSkin(skin: number, a: Arm): Layer {
  const L = newLayer(skinPal(skin), skinLine(skin));
  brush(L, a.sx, a.sy, a.ex, a.ey, 3, T.BASE);
  // round little hand
  L.ellipse(a.ex - 2, a.ey - (a.up ? 2 : 0), a.ex + 1, a.ey + (a.up ? 1 : 3), T.BASE);
  L.shade({ right: 1, bottom: 1, hi: false });
  return L;
}

function sleeve(pal: RGB[], a: Arm, frac: number, w: number): Layer {
  const L = newLayer(pal);
  const ex = Math.round(a.sx + (a.ex - a.sx) * frac), ey = Math.round(a.sy + (a.ey - a.sy) * frac);
  brush(L, a.sx, a.sy, ex, ey, w, T.BASE);
  L.shade({ right: 1, bottom: 1, hi: false });
  if (frac >= 0.8) {
    // cuff
    const o = Math.floor(w / 2);
    if (a.up) L.hline(ex - o, ex - o + w - 1, ey - o, T.SH);
    else L.hline(ex - o, ex - o + w - 1, ey + o, T.SH);
  }
  return L;
}

// ---------------------------------------------------------------- tops

const OVERSIZED = new Set(["hoodie", "puffer", "track", "varsity", "jacket", "windbreaker", "jersey", "flannel", "trench", "shredknit", "blazer", "moto", "crewneck"]);
const TEES = new Set(["tee", "crop", "startee", "skulltee", "battee", "babytee"]);
/** Tops that leave the shoulders or the midriff bare, so skin is drawn underneath them. */
const BARE = new Set(["tube", "butterfly", "velour", "jeanjacket"]);

// Small chest prints (8x6 max). Stamped with Layer.mark so they never read backwards.
const PRINTS: Record<string, string[]> = {
  startee: ["..b..", ".bbb.", "bbbbb", ".bbb.", ".b.b."],
  skulltee: [".bbb.", "bcbcb", "bbbbb", ".b.b."],
  battee: ["b......b", "bb.bb.bb", "bbbbbbbb", "b.bbbb.b", "...bb..."],
};
// Jersey number, front (small) and back (big)
const NUM_FRONT = ["bb.bb", ".b..b", "bb.bb", "b...b", "bb.bb"];
const NUM_BACK = ["bbb.bbb", "..b...b", "bbb.bbb", "b.....b", "bbb.bbb"];

function topLayers(p: Part, f: Frame): { body: Layer; sleeveFor: (a: Arm) => Layer | null } {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#ff4f9a"]);
  const L = newLayer(pal);
  const k = p.kind;
  const big = OVERSIZED.has(k);
  const x0 = big ? 14 : 15, x1 = big ? 25 : 24;
  const bottom = k === "tube" || k === "butterfly" ? WAIST_Y - 4 : k === "velour" ? WAIST_Y - 2 : k === "jeanjacket" ? WAIST_Y - 1 : k === "crop" ? WAIST_Y - 3 : k === "babytee" || k === "moto" ? WAIST_Y : k === "jersey" ? WAIST_Y + 3 : k === "blazer" ? WAIST_Y + 4 : k === "trench" ? LEG_Y + 4 : big ? WAIST_Y + 2 : WAIST_Y + 1;
  if (k === "butterfly") return butterflyTop(pal, f);
  const neck = k === "tube" ? SHOULDER_Y + 1 : SHOULDER_Y;
  if (k !== "tube") L.hline(x0 + 1, x1 - 1, SHOULDER_Y - 1, T.BASE);
  L.rect(x0, neck, x1 - x0 + 1, bottom - neck + 1, T.BASE);
  if (k === "trench") for (let yy = WAIST_Y + 2; yy <= bottom; yy++) L.hline(x0 - 1, x1 + 1, yy, T.BASE); // skirt flares past the hips
  if (k === "trench" && !f.back) for (let yy = WAIST_Y + 1; yy <= bottom; yy++) L.erase(yy > WAIST_Y + 2 ? 18 : 19, yy, yy > WAIST_Y + 2 ? 4 : 2, 1); // open below the belt
  if (k === "puffer") L.hline(x0 - 1, x1 + 1, SHOULDER_Y + 4, T.BASE).hline(x0 - 1, x1 + 1, SHOULDER_Y + 8, T.BASE);
  if (k === "blazer") L.hline(x0 - 1, x1 + 1, SHOULDER_Y, T.BASE).hline(x0 - 1, x1 + 1, SHOULDER_Y + 1, T.BASE); // padded shoulders
  L.shade({ right: 2, bottom: 1 });
  const y = SHOULDER_Y;
  if (!f.back) {
    switch (k) {
      case "hoodie":
        // hood bunched around the neck, drawstrings, kangaroo pocket
        L.hline(x0 + 2, x1 - 2, y - 2, T.BASE).hline(x0 + 1, x1 - 1, y - 1, T.HI);
        L.hline(x0 + 3, x1 - 3, y, T.DEEP).hline(x0 + 4, x1 - 4, y - 1, T.SH);
        L.vline(18, y + 1, y + 4, T.A).vline(21, y + 1, y + 5, T.A);
        L.hline(16, 23, y + 6, T.DEEP).vline(16, y + 6, y + 9, T.DEEP).vline(23, y + 6, y + 9, T.DEEP);
        if (p.accent2) L.rect(19, y + 2, 2, 2, T.B);
        break;
      case "tee":
      case "crop":
      case "startee":
      case "skulltee":
      case "battee":
        L.hline(18, 21, y - 1, T.DEEP).hline(18, 21, y, T.SH);
        if (PRINTS[k]) L.mark(17, y + 2, PRINTS[k], { b: T.B, c: T.BASE });
        else if (p.accent2) L.mark(18, y + 3, [".b.b", "bbbb", ".bb."], { b: T.B });
        break;
      case "windbreaker":
        // color-blocked shell: accent yoke across the chest, half-zip, elastic hem
        for (let yy = y - 1; yy <= y + 3; yy++) for (let xx = x0; xx <= x1; xx++) if (L.get(xx, yy)) L.set(xx, yy, T.A);
        L.hline(x0, x1, y + 4, T.B);
        L.hline(x0 + 2, x1 - 2, y - 2, T.BASE).hline(x0 + 3, x1 - 3, y - 1, T.DEEP);
        L.vline(19, y - 1, y + 5, T.DEEP).set(20, y + 5, T.W);
        L.hline(x0, x1, bottom, T.DEEP).hline(x0, x1, bottom - 1, T.SH);
        break;
      case "jersey":
        // oversized mesh tank: wide armholes, trim, number on the chest
        L.erase(x0, y - 1, 3, 4).erase(x1 - 2, y - 1, 3, 4).set(x0, y + 3, 0).set(x1, y + 3, 0);
        L.hline(17, 22, y - 1, 0).hline(18, 21, y, 0).hline(17, 22, y - 1, 0);
        L.hline(16, 16, y - 1, T.A).hline(23, 23, y - 1, T.A).hline(17, 17, y, T.A).hline(22, 22, y, T.A).hline(18, 21, y + 1, T.A);
        for (const xx of [x0 + 3, x1 - 3]) L.vline(xx, y - 1, y + 2, T.A);
        L.vline(x0, y + 4, y + 6, T.A).vline(x1, y + 4, y + 6, T.A);
        L.hline(x0, x1, bottom, T.A);
        L.mark(17, y + 4, NUM_FRONT, { b: T.B });
        break;
      case "flannel": {
        // open plaid overshirt over a tee in accent2
        for (let yy = y; yy <= bottom; yy++) for (let xx = x0; xx <= x1; xx++) {
          if (!L.get(xx, yy)) continue;
          if ((yy - y) % 4 === 2) L.set(xx, yy, T.DEEP);
          else if ((xx - x0) % 4 === 1) L.set(xx, yy, T.A);
        }
        L.rect(18, y, 4, bottom - y, T.B).hline(18, 21, y, T.SH);
        L.vline(17, y - 1, bottom, T.INK).vline(22, y - 1, bottom, T.INK);
        L.set(16, y - 1, T.BASE).set(23, y - 1, T.BASE);
        break;
      }
      case "babytee":
        // small fitted tee with a contrast ringer collar
        L.hline(17, 22, y - 1, T.A).hline(18, 21, y, T.A);
        if (p.accent2) L.mark(18, y + 3, [".b.b", "bbbb", ".bb."], { b: T.B });
        break;
      case "blazer":
        // oversized blazer, open over a top in accent2: notched lapels, one button, flap pockets
        L.rect(18, y, 4, 5, T.B).rect(19, y + 5, 2, WAIST_Y - y - 6, T.B);
        L.vline(17, y - 1, y + 4, T.SH).vline(22, y - 1, y + 4, T.SH).set(18, y + 5, T.SH).set(21, y + 5, T.SH);
        L.set(16, y, T.HI).set(23, y, T.HI).set(16, y + 1, T.DEEP).set(23, y + 1, T.DEEP); // lapel notches
        L.set(19, WAIST_Y - 1, T.A).vline(19, WAIST_Y, bottom, T.DEEP);
        L.hline(x0 + 1, x0 + 3, WAIST_Y + 1, T.DEEP).hline(x1 - 3, x1 - 1, WAIST_Y + 1, T.DEEP);
        L.hline(x0, x1, bottom, T.SH);
        break;
      case "moto": {
        // cropped leather biker jacket: wide collar, asymmetric zip, belted hem
        for (let yy = y; yy < bottom; yy++) {
          const zx = Math.round(22 - ((yy - y) * 5) / (bottom - y - 1));
          L.set(zx, yy, T.A).set(zx + 1, yy, T.DEEP);
        }
        L.hline(16, 18, y, T.HI).hline(21, 23, y, T.HI).set(16, y + 1, T.HI).set(23, y + 1, T.HI);
        leatherShine(L, x0 + 1, y + 2, bottom - 1);
        L.hline(x0, x1, bottom, T.DEEP).set(x0 + 2, bottom, T.A);
        break;
      }
      case "tube":
        // strapless stretch tube top: ruched across the chest, a shine on the left
        for (let yy = y + 2; yy < bottom; yy += 2) L.hline(x0 + 1, x1 - 2, yy, T.SH);
        L.vline(x0 + 1, y + 2, bottom - 1, T.HI);
        L.hline(x0, x1, y + 1, T.HI);
        break;
      case "velour":
        // fitted velour zip hoodie, cropped above the waist: hood at the neck, zip with a pull, soft pile sheen
        L.hline(x0 + 2, x1 - 2, y - 2, T.BASE).hline(x0 + 1, x1 - 1, y - 1, T.HI);
        L.hline(x0 + 3, x1 - 3, y, T.SH);
        velourSheen(L, x0, x1, y + 1, bottom - 1);
        L.vline(19, y, bottom, T.DEEP).set(20, y + 1, T.A).set(20, y + 2, T.A);
        L.hline(x0, x1, bottom, T.SH);
        break;
      case "jeanjacket":
        // cropped denim jacket: pointed collar, button placket, two flap chest pockets, a waistband
        L.set(17, y - 1, T.HI).set(22, y - 1, T.HI).set(17, y, T.HI).set(22, y, T.HI).set(18, y, T.HI).set(21, y, T.HI);
        L.vline(19, y, bottom, T.DEEP);
        for (let yy = y + 2; yy < bottom; yy += 3) L.set(20, yy, T.A);
        for (const px of [16, 21]) L.hline(px, px + 2, y + 2, T.DEEP).hline(px, px + 2, y + 3, T.SH).set(px + 1, y + 3, T.A);
        L.hline(x0, x1, bottom - 1, T.SH).hline(x0, x1, bottom, T.DEEP).set(x0 + 1, bottom, T.A);
        L.vline(x0 + 1, y + 4, bottom - 2, T.HI);
        break;
      case "crewneck":
        // oversized sweatshirt: ribbed crew collar, ribbed hem, a soft fold across the middle
        L.hline(17, 22, y - 1, T.SH).hline(18, 21, y, T.DEEP);
        L.hline(16, 18, y + 6, T.SH).hline(21, 23, y + 7, T.SH);
        for (let x = x0; x <= x1; x++) L.set(x, bottom, x % 2 ? T.SH : T.DEEP);
        break;
      case "tank":
        L.erase(x0, y - 1, 2, 2).erase(x1 - 1, y - 1, 2, 2);
        L.hline(18, 21, y - 1, 0).hline(18, 21, y, T.DEEP);
        break;
      case "puffer":
        L.hline(x0 - 1, x1 + 1, y + 4, T.DEEP).hline(x0 - 1, x1 + 1, y + 8, T.DEEP);
        L.vline(19, y, bottom, T.DEEP);
        L.hline(x0 + 2, x1 - 2, y - 2, T.BASE).hline(x0 + 1, x1 - 1, y - 1, T.HI).hline(x0 + 3, x1 - 3, y, T.SH);
        break;
      case "track":
        L.vline(19, y, bottom, T.DEEP).vline(20, y, bottom, T.A);
        L.hline(17, 22, y - 1, T.A);
        break;
      case "varsity":
        L.vline(19, y, bottom, T.DEEP);
        L.hline(x0, x1, bottom, T.A).hline(16, 23, y - 1, T.A);
        L.mark(16, y + 3, ["bb", "b.", "bb"], { b: T.B }); // varsity letter
        break;
      case "jacket": // open jacket over a white tee
        L.rect(18, y, 4, bottom - y, T.W).hline(18, 21, y, T.A);
        L.vline(17, y, bottom, T.DEEP).vline(22, y, bottom, T.DEEP);
        break;
      case "trench":
        // long leather coat, open over a tee in accent2: wide lapels, a belt, and a slick shine
        L.rect(18, y, 4, WAIST_Y - y + 1, T.B).hline(18, 21, y, T.DEEP);
        L.set(16, y - 1, T.HI).set(23, y - 1, T.HI).set(17, y, T.HI).set(22, y, T.HI);
        L.vline(17, y + 1, WAIST_Y, T.DEEP).vline(22, y + 1, WAIST_Y, T.DEEP);
        L.vline(17, WAIST_Y + 1, bottom, T.DEEP).vline(22, WAIST_Y + 1, bottom, T.DEEP);
        L.hline(x0, 17, WAIST_Y, T.DEEP).hline(22, x1, WAIST_Y, T.DEEP).set(x0 + 1, WAIST_Y, T.A);
        leatherShine(L, x0 + 1, y + 1, bottom - 1);
        L.hline(x0 - 1, 17, bottom, T.DEEP).hline(22, x1 + 1, bottom, T.DEEP);
        break;
      case "shredknit": {
        // oversized knit with ladder holes, a ribbed crew neck, optional stripes in accent, ragged hem
        L.hline(17, 22, y - 1, T.DEEP).hline(17, 22, y, T.SH);
        knit(L, p, y + 1, bottom);
        for (const [hx, hy] of [[16, 4], [22, 6], [18, 8]]) L.hline(hx, hx + 1, y + hy, T.INK).set(hx + 1, y + hy + 1, T.DEEP);
        break;
      }
      case "mesh":
        // sheer net long-sleeve over an undershirt in accent2
        L.hline(18, 21, y - 1, T.DEEP).hline(18, 21, y, T.SH);
        mesh(L, y + 1, bottom - 1);
        L.hline(x0, x1, bottom, T.DEEP);
        break;
    }
    if (big && !["jacket", "windbreaker", "jersey", "flannel", "trench", "shredknit", "blazer", "moto", "crewneck"].includes(k)) L.hline(x0, x1, bottom, T.SH);
  } else {
    if (k === "windbreaker") { for (let yy = y - 1; yy <= y + 3; yy++) for (let xx = x0; xx <= x1; xx++) if (L.get(xx, yy)) L.set(xx, yy, T.A); L.hline(x0, x1, y + 4, T.B).hline(x0, x1, bottom, T.DEEP); }
    if (k === "jersey") {
      L.erase(x0, y - 1, 3, 4).erase(x1 - 2, y - 1, 3, 4).set(x0, y + 3, 0).set(x1, y + 3, 0);
      L.hline(17, 22, y - 1, T.A);
      for (const xx of [x0 + 3, x1 - 3]) L.vline(xx, y - 1, y + 2, T.A);
      L.vline(x0, y + 4, y + 6, T.A).vline(x1, y + 4, y + 6, T.A).hline(x0, x1, bottom, T.A);
      L.mark(16, y + 2, NUM_BACK, { b: T.B });
    }
    if (k === "flannel") for (let yy = y; yy <= bottom; yy++) for (let xx = x0; xx <= x1; xx++) {
      if (!L.get(xx, yy)) continue;
      if ((yy - y) % 4 === 2) L.set(xx, yy, T.DEEP);
      else if ((xx - x0) % 4 === 1) L.set(xx, yy, T.A);
    }
    if (k === "hoodie") L.ellipse(15, y - 2, 24, y + 5, T.SH).ellipse(16, y - 2, 23, y + 3, T.BASE).hline(17, 22, y + 4, T.DEEP);
    if (k === "varsity") L.stamp(17, y + 3, ["bbbbbb", "b....b", "bbbbbb"], { b: T.B });
    if (k === "track") for (const xx of [x0 + 1, x1 - 1]) L.vline(xx, y, bottom, T.A);
    if (k === "trench") {
      L.hline(x0, x1, WAIST_Y, T.DEEP).vline(19, WAIST_Y + 3, bottom, T.DEEP); // belt and back vent
      leatherShine(L, x0 + 1, y + 1, bottom - 1);
    }
    if (k === "shredknit") { L.hline(17, 22, y - 1, T.SH); knit(L, p, y + 1, bottom); L.hline(20, 21, y + 5, T.INK).hline(16, 17, y + 7, T.INK); }
    if (k === "mesh") { mesh(L, y, bottom - 1); L.hline(x0, x1, bottom, T.DEEP); }
    if (k === "babytee") L.hline(17, 22, y - 1, T.A);
    if (k === "blazer") { L.hline(x0, x1, y + 1, T.SH).vline(19, bottom - 3, bottom, T.DEEP).hline(x0, x1, bottom, T.SH); }
    if (k === "moto") { leatherShine(L, x0 + 1, y + 1, bottom - 1); L.hline(x0, x1, bottom, T.DEEP); }
    if (k === "tube") { for (let yy = y + 2; yy < bottom; yy += 2) L.hline(x0 + 1, x1 - 2, yy, T.SH); L.hline(x0, x1, y + 1, T.HI); }
    if (k === "velour") { L.ellipse(15, y - 2, 24, y + 4, T.SH).ellipse(16, y - 2, 23, y + 2, T.BASE); velourSheen(L, x0, x1, y + 5, bottom - 1); L.hline(x0, x1, bottom, T.SH); }
    if (k === "jeanjacket") { L.hline(17, 22, y - 1, T.HI); L.hline(x0, x1, y + 3, T.SH).vline(17, y + 4, bottom - 1, T.SH).vline(22, y + 4, bottom - 1, T.SH); L.hline(x0, x1, bottom, T.DEEP); }
    if (k === "crewneck") { L.hline(17, 22, y - 1, T.SH); for (let x = x0; x <= x1; x++) L.set(x, bottom, x % 2 ? T.SH : T.DEEP); }
  }
  const frac = TEES.has(k) ? 0.35 : k === "tank" || k === "jersey" || k === "tube" ? 0 : 1;
  const w = big ? 5 : 4;
  return {
    body: L,
    sleeveFor: (a) => {
      if (!frac) return null;
      const S = sleeve(pal, a, frac, w);
      if (k === "track") brush(S, a.sx + a.side * 1, a.sy, Math.round(a.sx + (a.ex - a.sx) * 0.8) + a.side, Math.round(a.sy + (a.ey - a.sy) * 0.8), 1, T.A);
      if (k === "windbreaker") { const mx = Math.round(a.sx + (a.ex - a.sx) * 0.45), my = Math.round(a.sy + (a.ey - a.sy) * 0.45); S.rect(mx - 2, my - 1, 5, 2, T.A).clipTo(sleeve(pal, a, frac, w)); }
      if (k === "flannel") for (let i = 0; i < S.data.length; i++) if (S.data[i] && Math.floor(i / SW) % 4 === 2) S.data[i] = T.DEEP;
      if (k === "puffer") { const mx = Math.round((a.sx + a.ex) / 2), my = Math.round((a.sy + a.ey) / 2); S.hline(mx - 2, mx + 2, my, T.DEEP); }
      if (k === "babytee") { const ex = Math.round(a.sx + (a.ex - a.sx) * frac), ey = Math.round(a.sy + (a.ey - a.sy) * frac); S.rect(ex - 2, ey + 1, 4, 2, T.A).clipTo(sleeve(pal, a, frac, w)); }
      if (k === "crewneck") { const ex = Math.round(a.sx + (a.ex - a.sx) * frac), ey = Math.round(a.sy + (a.ey - a.sy) * frac); S.hline(ex - 2, ex + 2, a.up ? ey - 1 : ey + 1, T.SH); }
      if (k === "trench" || k === "moto") for (let i = SW; i < S.data.length; i++) if (S.data[i] === T.BASE && !S.data[i - 1] && Math.floor(i / SW) % 3) S.data[i] = T.HI;
      if (k === "mesh") mesh(S, 0, SH - 1);
      if (k === "velour") for (let i = SW; i < S.data.length; i++) if (S.data[i] === T.BASE && !S.data[i - 1] && Math.floor(i / SW) % 2) S.data[i] = T.HI;
      if (k === "jeanjacket") { const ex = Math.round(a.sx + (a.ex - a.sx) * frac), ey = Math.round(a.sy + (a.ey - a.sy) * frac); S.hline(ex - 2, ex + 2, a.up ? ey - 1 : ey + 1, T.DEEP); }
      if (k === "shredknit") {
        for (let i = 0; i < S.data.length; i++) if (S.data[i] === T.BASE && (i % SW) % 2 === 0) S.data[i] = T.SH;
        if (p.accent) { const mx = Math.round(a.sx + (a.ex - a.sx) * 0.5), my = Math.round(a.sy + (a.ey - a.sy) * 0.5); S.rect(mx - 2, my, 5, 1, T.A).clipTo(sleeve(pal, a, frac, w)); }
      }
      return S;
    },
  };
}

/**
 * Butterfly halter: a sequinned butterfly across the chest, held up by a string around the neck and one
 * across the back. Symmetric, so it reads the same from either side.
 */
function butterflyTop(pal: RGB[], f: Frame): { body: Layer; sleeveFor: (a: Arm) => Layer | null } {
  const L = newLayer(pal);
  const y = SHOULDER_Y;
  if (f.back) {
    L.hline(15, 24, y + 3, T.BASE).hline(18, 21, y - 1, T.BASE).set(19, y, T.SH).set(20, y, T.SH);
    L.rect(19, y + 2, 2, 3, T.SH); // the bow
    return { body: L, sleeveFor: () => null };
  }
  const key = { b: T.BASE, s: T.SH, a: T.A, d: T.DEEP, h: T.HI };
  L.stamp(14, y - 1, [
    ".....dd.....",
    "....d..d....",
    ".hhhb..bbbb.",
    "hbabbddbbabb",
    "hbbbbddbbbbs",
    ".hbbbddbbbs.",
    "..bbbddbbs..",
    "..bab..bab..",
    "..bb....bs..",
  ], key);
  return { body: L, sleeveFor: () => null };
}

/** Velour: a soft pile that catches the light in short vertical dashes. */
function velourSheen(L: Layer, x0: number, x1: number, y0: number, y1: number) {
  for (let y = y0; y <= y1; y++) for (const x of [x0 + 1, x0 + 4, x1 - 3]) if (L.get(x, y) === T.BASE && y % 3 !== 2) L.set(x, y, T.HI);
}

/** Leather: a broken vertical highlight down the left side, like light catching the hide. */
function leatherShine(L: Layer, x: number, y0: number, y1: number) {
  for (let y = y0; y <= y1; y++) if (L.get(x, y) === T.BASE && y % 5 !== 3) L.set(x, y, T.HI);
}

/** Chunky knit: vertical ribs, plus stripes in accent when the item has one. */
function knit(L: Layer, p: Part, y0: number, y1: number) {
  for (let y = y0; y <= y1; y++) for (let x = 0; x < SW; x++) {
    const v = L.get(x, y);
    if (v !== T.BASE && v !== T.HI && v !== T.SH) continue;
    if (p.accent && (y - y0) % 4 === 2) L.set(x, y, T.A);
    else if (x % 2 === 0) L.set(x, y, T.SH);
  }
  for (let x = 0; x < SW; x++) if (L.get(x, y1) && x % 3 === 1) L.set(x, y1, 0); // ragged hem
}

/** Net weave: the undershirt (accent2) shows through a grid of holes. */
function mesh(L: Layer, y0: number, y1: number) {
  for (let y = y0; y <= y1; y++) for (let x = 0; x < SW; x++) {
    const v = L.get(x, y);
    if ((v === T.BASE || v === T.HI || v === T.SH) && x % 2 === 1 && y % 2 === 0) L.set(x, y, T.B);
  }
}

// ---------------------------------------------------------------- bottoms

/**
 * `toes`: the shoes are cut low (ballet flats), so the skin carries on one row into the shoe.
 * `bare`: open sandals (flip-flops), so the whole foot is drawn, toes pointing outward.
 */
function legs(skin: number, f: Frame, toes = false, bare = false): Layer {
  const L = newLayer(skinPal(skin), skinLine(skin));
  const ly = f.step === 1 ? -1 : 0, ry = f.step === -1 ? -1 : 0;
  const h = (toes || bare ? SHOE_Y + 1 : HEM_Y) - HIP_Y + 1;
  L.rect(16, HIP_Y + ly, 3, h, T.BASE).rect(21, HIP_Y + ry, 3, h, T.BASE);
  if (bare) L.hline(14, 18, SHOE_Y + 1 + ly, T.BASE).hline(21, 25, SHOE_Y + 1 + ry, T.BASE);
  L.shade({ right: 1, bottom: 0, hi: false });
  return L;
}

/** Bare skin under tops that show the shoulders or midriff, and above low-rise waistbands. */
function torso(skin: number): Layer {
  const L = newLayer(skinPal(skin), skinLine(skin));
  L.hline(16, 23, SHOULDER_Y - 1, T.BASE).rect(15, SHOULDER_Y, 10, HIP_Y - SHOULDER_Y + 1, T.BASE);
  L.shade({ right: 1, bottom: 0, hi: false });
  L.set(19, WAIST_Y, T.SH); // belly button
  return L;
}

function bottomLayer(p: Part, f: Frame, tuck: number | null = null): Layer {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#000000"]);
  const L = newLayer(pal);
  const ly = f.step === 1 ? -1 : 0, ry = f.step === -1 ? -1 : 0;
  const k = p.kind;
  if (LOW_RISE.has(k)) return lowRise(L, p, ly, ry);
  L.rect(15, WAIST_Y, 10, LEG_Y - WAIST_Y, T.BASE);
  if (k === "skirt") {
    L.spans([[LEG_Y, 14, 25], [LEG_Y + 1, 14, 25], [LEG_Y + 2, 13, 26], [LEG_Y + 3, 13, 26]]);
    L.shade({ right: 2, bottom: 1 });
    for (let x = 15; x < 26; x += 3) L.vline(x, LEG_Y, LEG_Y + 3, T.SH);
    if (p.accent) for (let x = 13; x <= 26; x += 2) L.set(x, LEG_Y + 2, T.A);
    L.hline(15, 24, WAIST_Y, T.DEEP);
    return L;
  }
  if (k === "shorts") {
    L.rect(15, LEG_Y + ly, 5, 3, T.BASE).rect(20, LEG_Y + ry, 5, 3, T.BASE);
    L.shade({ right: 2, bottom: 1 });
    L.vline(19, LEG_Y, LEG_Y + 2, T.DEEP).hline(15, 24, WAIST_Y, T.SH);
    return L;
  }
  if (k === "jorts") {
    // long, wide denim shorts that end just under the knee
    const end = LEG_Y + 4;
    L.rect(14, LEG_Y + ly, 6, end - LEG_Y + 1, T.BASE).rect(20, LEG_Y + ry, 6, end - LEG_Y + 1, T.BASE);
    L.shade({ right: 2, bottom: 1 });
    L.vline(19, LEG_Y + ly, end + ly, T.DEEP).hline(15, 24, WAIST_Y, T.SH);
    L.hline(14, 19, end + ly, T.SH).hline(20, 25, end + ry, T.SH);
    L.hline(15, 17, LEG_Y + 1 + ly, T.HI).hline(21, 23, LEG_Y + 1 + ry, T.HI);
    return L;
  }
  if (k === "flares") {
    // slim through the thigh, flaring wide at the hem and stacking over the shoe; leather shines, denim (accent) is ripped
    for (const [a, b, dy] of [[15, 19, ly], [20, 24, ry]] as const) {
      for (let yy = LEG_Y; yy <= SHOE_Y + 1; yy++) {
        const fl = yy < HEM_Y - 3 ? 0 : yy < HEM_Y - 1 ? 1 : 2;
        L.hline(a < 20 ? a - fl : a, a < 20 ? b : b + fl, yy + dy, T.BASE);
      }
    }
    L.shade({ right: 2, bottom: 1 });
    L.vline(19, LEG_Y + ly, SHOE_Y + 1 + ly, T.DEEP).hline(15, 24, WAIST_Y, T.SH);
    L.hline(13, 19, SHOE_Y + 1 + ly, T.DEEP).hline(20, 26, SHOE_Y + 1 + ry, T.DEEP);
    if (p.accent) {
      // knee and thigh rips: white threads across a dark gap
      for (const [rx, ry0, dy] of [[16, LEG_Y + 4, ly], [21, LEG_Y + 5, ry], [21, LEG_Y + 1, ry]] as const) {
        L.hline(rx, rx + 2, ry0 + dy, T.A).hline(rx, rx + 2, ry0 + 1 + dy, T.DEEP);
      }
    } else {
      leatherShine(L, 16, LEG_Y + ly, HEM_Y + ly);
      leatherShine(L, 21, LEG_Y + ry, HEM_Y + ry);
    }
    return L;
  }
  if (k === "mini") {
    // short leather mini skirt sitting high on the thigh, with a side zip
    L.spans([[LEG_Y, 14, 25], [LEG_Y + 1, 14, 25]]);
    L.shade({ right: 2, bottom: 1 });
    L.hline(15, 24, WAIST_Y, T.DEEP);
    leatherShine(L, 15, WAIST_Y + 1, LEG_Y + 1);
    L.vline(23, WAIST_Y + 1, LEG_Y, T.A);
    return L;
  }
  if (k === "wideleg" || k === "trousers") {
    // wide straight legs from the hip that pool over the shoes; denim is faded, trousers are pressed
    for (const [a, b, dy] of [[14, 19, ly], [20, 25, ry]] as const) {
      for (let yy = LEG_Y; yy <= SHOE_Y + 1; yy++) {
        const fl = yy >= HEM_Y ? 1 : 0;
        L.hline(a < 20 ? a - fl : a, a < 20 ? b : b + fl, yy + dy, T.BASE);
      }
    }
    L.shade({ right: 2, bottom: 1 });
    L.vline(19, LEG_Y + ly, SHOE_Y + 1 + ly, T.DEEP);
    L.hline(13, 19, SHOE_Y + 1 + ly, T.SH).hline(20, 26, SHOE_Y + 1 + ry, T.SH);
    if (k === "trousers") {
      L.hline(15, 24, WAIST_Y, T.DEEP).set(17, WAIST_Y + 1, T.SH).set(22, WAIST_Y + 1, T.SH); // waistband and pleats
      L.vline(20, LEG_Y + 1 + ry, SHOE_Y + ry, T.SH); // a clear inseam so the legs read apart
      L.hline(15, 17, LEG_Y + 1 + ly, T.HI).hline(21, 23, LEG_Y + 1 + ry, T.HI);
    } else {
      L.hline(15, 24, WAIST_Y, T.SH);
      L.rect(15, LEG_Y + 1 + ly, 3, 3, T.HI).rect(21, LEG_Y + 1 + ry, 3, 3, T.HI); // worn-in fade on the thighs
      L.hline(15, 17, LEG_Y + 6 + ly, T.SH).hline(21, 23, LEG_Y + 7 + ry, T.SH);
    }
    return L;
  }
  const baggy = k === "baggy" || k === "cargo" || k === "camo" || k === "parachute";
  const lx0 = baggy ? 14 : 15, rx1 = baggy ? 25 : 24;
  L.rect(lx0, LEG_Y + ly, 20 - lx0, HEM_Y - LEG_Y + 1, T.BASE);
  L.rect(20, LEG_Y + ry, rx1 - 19, HEM_Y - LEG_Y + 1, T.BASE);
  if (baggy) L.hline(lx0 - 1, 19, HEM_Y + ly, T.BASE).hline(20, rx1 + 1, HEM_Y + ry, T.BASE); // stacked hems
  L.shade({ right: 2, bottom: 1 });
  L.vline(19, LEG_Y + ly, HEM_Y + ly, T.DEEP);
  L.hline(15, 24, WAIST_Y, T.SH);
  if (k === "baggy") {
    L.hline(15, 17, LEG_Y + 1 + ly, T.HI).hline(21, 23, LEG_Y + 1 + ry, T.HI);
    L.hline(16, 18, LEG_Y + 4 + ly, T.SH).hline(21, 23, LEG_Y + 5 + ry, T.SH);
  } else if (k === "parachute") {
    // nylon: knee creases, a side strap, cinched toggles at the ankle
    L.vline(lx0, LEG_Y + 1 + ly, HEM_Y - 1 + ly, T.A).vline(rx1, LEG_Y + 1 + ry, HEM_Y - 1 + ry, T.A);
    L.hline(15, 18, LEG_Y + 3 + ly, T.SH).hline(21, 24, LEG_Y + 4 + ry, T.SH);
    L.hline(16, 18, LEG_Y + 1 + ly, T.HI).hline(21, 23, LEG_Y + 1 + ry, T.HI);
    L.hline(lx0 - 1, 19, HEM_Y + ly, T.DEEP).hline(20, rx1 + 1, HEM_Y + ry, T.DEEP);
    L.set(lx0, HEM_Y - 1 + ly, T.B).set(rx1, HEM_Y - 1 + ry, T.B);
  } else if (k === "camo") {
    // cargo cut with a blotchy two-color camo
    const blot = [[15, 0, 2], [21, 1, 3], [17, 3, 1], [23, 4, 2], [14, 6, 3], [20, 6, 1], [16, 8, 2], [22, 9, 3], [18, -2, 3], [15, -3, 1], [22, -3, 2]];
    for (const [bx, by, c] of blot) {
      const v = c === 1 ? T.A : c === 2 ? T.B : T.SH, dy = bx < 20 ? ly : ry;
      for (const [dx, dd] of [[0, 0], [1, 0], [2, 0], [1, 1], [0, 1], [2, -1]]) {
        const xx = bx + dx, yy = LEG_Y + by + dd + dy;
        if (L.get(xx, yy) === T.BASE || L.get(xx, yy) === T.HI) L.set(xx, yy, v);
      }
    }
    L.rect(lx0, LEG_Y + 2 + ly, 3, 3, T.SH).hline(lx0, lx0 + 2, LEG_Y + 2 + ly, T.DEEP);
    L.rect(rx1 - 2, LEG_Y + 2 + ry, 3, 3, T.SH).hline(rx1 - 2, rx1, LEG_Y + 2 + ry, T.DEEP);
  } else if (k === "cargo") {
    L.rect(lx0, LEG_Y + 2 + ly, 3, 3, T.SH).hline(lx0, lx0 + 2, LEG_Y + 2 + ly, T.DEEP);
    L.rect(rx1 - 2, LEG_Y + 2 + ry, 3, 3, T.SH).hline(rx1 - 2, rx1, LEG_Y + 2 + ry, T.DEEP);
  } else if (k === "straps") {
    // punk strap pants: a strap between the knees, a hanging loop at the hip, zips up the shins
    for (let x = 17; x <= 22; x++) L.set(x, LEG_Y + 3 + (x < 20 ? ly : ry), T.A);
    L.set(17, LEG_Y + 3 + ly, T.B).set(22, LEG_Y + 3 + ry, T.B);
    L.vline(lx0, WAIST_Y + 1, LEG_Y + 3 + ly, T.A).set(lx0, LEG_Y + 4 + ly, T.B);
    L.vline(17, LEG_Y + 6 + ly, HEM_Y - 1 + ly, T.B).vline(22, LEG_Y + 6 + ry, HEM_Y - 1 + ry, T.B);
  } else if (k === "trackpants") {
    L.vline(15, LEG_Y + ly, HEM_Y + ly, T.A).vline(24, LEG_Y + ry, HEM_Y + ry, T.A);
  } else if (k === "plaid") {
    for (let y = WAIST_Y + 1; y <= HEM_Y; y += 3) for (let x = 14; x <= 25; x++) if (L.get(x, y)) L.set(x, y, T.SH);
    for (let x = 16; x <= 25; x += 3) for (let y = WAIST_Y; y <= HEM_Y; y++) if (L.get(x, y)) L.set(x, y, T.A);
  }
  if (tuck !== null) tuckInto(L, tuck, lx0, rx1, ly, ry, baggy);
  return L;
}

/** Bottoms that sit low on the hips, below the navel. */
const LOW_RISE = new Set(["lowrise", "velourpants", "denimmini"]);

/**
 * Low-rise bottoms start a couple of rows under the usual waist, so a bare midriff shows above them.
 * Denim flares wear a thin belt (accent); velour pants have a soft fold-over band; the denim mini is frayed.
 */
function lowRise(L: Layer, p: Part, ly: number, ry: number): Layer {
  const k = p.kind, w = LEG_Y - 1;
  L.rect(15, w, 10, 1, T.BASE);
  if (k === "denimmini") {
    L.spans([[LEG_Y, 14, 25], [LEG_Y + 1, 14, 25], [LEG_Y + 2, 14, 25]]);
    L.shade({ right: 2, bottom: 1 });
    L.hline(15, 24, w, T.A).set(17, w, T.DEEP);
    L.vline(19, w + 1, LEG_Y + 1, T.DEEP).set(18, LEG_Y + 1, T.SH);
    L.hline(15, 17, LEG_Y, T.HI).hline(21, 22, LEG_Y, T.HI);
    for (let x = 14; x <= 25; x++) if (x % 2) L.set(x, LEG_Y + 2, T.HI); // frayed hem
    return L;
  }
  // slim through the thigh, flaring out at the knee and pooling over the shoe
  for (const [a, b, dy] of [[15, 19, ly], [20, 24, ry]] as const) {
    for (let yy = LEG_Y; yy <= SHOE_Y + 1; yy++) {
      const fl = yy < HEM_Y - 4 ? 0 : yy < HEM_Y - 1 ? 1 : 2;
      L.hline(a < 20 ? a - fl : a, a < 20 ? b : b + fl, yy + dy, T.BASE);
    }
  }
  L.shade({ right: 2, bottom: 1 });
  L.vline(19, LEG_Y + ly, SHOE_Y + 1 + ly, T.DEEP);
  L.hline(13, 19, SHOE_Y + 1 + ly, T.DEEP).hline(20, 26, SHOE_Y + 1 + ry, T.DEEP);
  if (k === "velourpants") {
    L.hline(15, 24, w, T.SH);
    for (const [x, dy] of [[16, ly], [21, ry]] as const) for (let yy = LEG_Y + 1; yy < HEM_Y; yy++) if (yy % 3 !== 2) L.set(x, yy + dy, T.HI);
  } else {
    L.hline(15, 24, w, T.A).rect(18, w, 2, 1, T.B); // thin belt and buckle
    L.rect(15, LEG_Y + 1 + ly, 3, 3, T.HI).rect(21, LEG_Y + 1 + ry, 3, 3, T.HI); // faded thighs
    L.hline(15, 17, LEG_Y + 6 + ly, T.SH).hline(21, 23, LEG_Y + 7 + ry, T.SH);
  }
  return L;
}

/**
 * Long pants worn with tall shoes: the legs stop where the shoe begins (nothing peeks out beside it)
 * and the fabric bunches one pixel wider just above the collar, like pants tucked or stacked into boots.
 */
function tuckInto(L: Layer, top: number, lx0: number, rx1: number, ly: number, ry: number, baggy: boolean) {
  for (let x = 0; x < SW; x++) {
    const t = top + (x < 20 ? ly : ry);
    for (let y = t; y < SH; y++) L.set(x, y, 0);
  }
  for (const [a, b, dy] of [[lx0 - 1, 19, ly], [20, rx1 + 1, ry]] as const) {
    const y = top + dy - 1;
    L.hline(a, b, y, T.SH).hline(a + 1, b - 1, y - 1, T.BASE);
    L.set(a, y - 1, baggy ? T.BASE : T.SH).set(b, y - 1, T.SH);
    L.set(a < 20 ? b : a, y, T.DEEP); // the seam between the legs
  }
}

// ---------------------------------------------------------------- shoes

/**
 * Top row of a shoe that rises above the ankle (boots, high-tops, socks), or null for low shoes.
 * Long pants tuck into these instead of being covered by a block pasted over the hem.
 */
export function shoeTop(kind: string): number | null {
  return kind === "platsneaks" ? SHOE_Y - 1 : kind === "platforms" ? SHOE_Y - 4 : kind === "boots" || kind === "slides" || kind === "bulky" ? SHOE_Y - 3 : kind === "hightops" ? SHOE_Y - 2 : null;
}

// Tall shoes sit on each leg: the shaft hugs the leg and the toe sticks out one pixel to the outside,
// mirrored left and right so both feet line up under the pant legs.
const TALL_FEET = [
  { shaft: [14, 18], sock: [15, 18], sole: [13, 18] },
  { shaft: [21, 25], sock: [21, 24], sole: [21, 26] },
] as const;

function shoesLayer(p: Part, f: Frame): Layer {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#d8d8e0"]);
  const L = newLayer(pal);
  const ly = f.step === 1 ? -1 : 0, ry = f.step === -1 ? -1 : 0;
  if (p.kind === "platsneaks") {
    // chunky platform sneakers: a padded upper and a sole two rows deep with a stripe in accent
    for (const [x, dy] of [[13, ly], [21, ry]] as const) {
      L.rect(x + 1, SHOE_Y - 1 + dy, 5, 2, T.BASE).rect(x, SHOE_Y + dy, 7, 2, T.BASE);
      L.rect(x, SHOE_Y + 2 + dy, 7, 2, T.B);
    }
    L.shade({ right: 1, bottom: 0 });
    for (const [x, dy] of [[13, ly], [21, ry]] as const) {
      L.rect(x, SHOE_Y + 2 + dy, 7, 2, T.B).hline(x, x + 6, SHOE_Y + 2 + dy, T.A);
      L.set(x + 3, SHOE_Y + dy, T.A).set(x + 4, SHOE_Y + dy, T.A).hline(x + 2, x + 4, SHOE_Y - 1 + dy, T.SH);
    }
    return L;
  }
  if (shoeTop(p.kind) !== null) {
    TALL_FEET.forEach(({ shaft: [s0, s1], sock: [k0, k1], sole: [o0, o1] }, i) => {
      const dy = i ? ry : ly, top = shoeTop(p.kind)! + dy;
      if (p.kind === "slides") L.rect(k0, top, k1 - k0 + 1, SHOE_Y + 1 + dy - top, T.W);
      else L.rect(s0, top, s1 - s0 + 1, SHOE_Y + 1 + dy - top, T.BASE);
      L.rect(o0, SHOE_Y + 1 + dy, o1 - o0 + 1, 2, T.BASE);
    });
    L.shade({ right: 1, bottom: 0 });
    TALL_FEET.forEach(({ shaft: [s0, s1], sock: [k0, k1], sole: [o0, o1] }, i) => {
      const dy = i ? ry : ly, mid = (s0 + s1) >> 1, toe = i ? o1 : o0, top = shoeTop(p.kind)! + dy;
      if (p.kind === "slides") {
        // crew socks with a stripe, a chunky slide strap over the toes
        L.hline(k0, k1, SHOE_Y - 2 + dy, T.A).set(i ? k1 : k0, SHOE_Y + dy, T.SH);
        L.hline(o0, o1, SHOE_Y + 1 + dy, T.BASE).set(mid, SHOE_Y + 1 + dy, T.HI);
        L.hline(o0, o1, SHOE_Y + 3 + dy, T.B);
      } else if (p.kind === "hightops") {
        L.hline(s0, s1, SHOE_Y - 2 + dy, T.SH).set(mid, SHOE_Y - 1 + dy, T.A).set(mid, SHOE_Y + dy, T.A);
        L.hline(o0, o1, SHOE_Y + 1 + dy, T.A).set(toe, SHOE_Y + 2 + dy, T.HI);
        L.hline(o0, o1, SHOE_Y + 3 + dy, T.B);
      } else if (p.kind === "platforms") {
        // stacked platform boots: two buckled straps, a tall sole in accent2
        for (const sy of [top + 1, SHOE_Y - 1 + dy]) L.hline(s0, s1, sy, T.SH).set(i ? s1 : s0, sy, T.A);
        L.hline(o0, o1, SHOE_Y + 2 + dy, T.B).hline(o0, o1, SHOE_Y + 3 + dy, T.B);
      } else if (p.kind === "bulky") {
        // padded high-tops: puffy collar, a big tongue sticking out the top, laces, thick sole
        L.hline(s0, s1, top, T.SH).rect(mid - 1, top - 2, 3, 2, T.BASE).set(mid - 1, top - 2, T.HI);
        for (let ly2 = top + 1; ly2 <= SHOE_Y + dy; ly2 += 2) L.set(mid, ly2, T.W);
        L.hline(o0, o1, SHOE_Y + 1 + dy, T.A).hline(o0, o1, SHOE_Y + 2 + dy, T.A).set(toe, SHOE_Y + 1 + dy, T.HI);
        L.hline(o0, o1, SHOE_Y + 3 + dy, T.B);
      } else {
        // combat boots: a strap band across the shaft, heavy lug sole
        L.hline(s0, s1, SHOE_Y - 1 + dy, T.SH);
        L.hline(o0, o1, SHOE_Y + 3 + dy, T.DEEP);
      }
    });
    return L;
  }
  if (p.kind === "flats" || p.kind === "loafers") {
    // drawn for the left foot and mirrored onto the right, so the toes point outward on both
    const flats = p.kind === "flats";
    const rows: [number, number, number][] = flats
      ? [[SHOE_Y + 1, 15, 19], [SHOE_Y + 2, 13, 19], [SHOE_Y + 3, 13, 19]]
      : [[SHOE_Y + 1, 14, 19], [SHOE_Y + 2, 13, 19], [SHOE_Y + 3, 13, 19]];
    const foot = (x: number, i: number) => (i ? SW - 1 - x : x);
    const span = (y: number, a: number, b: number, i: number, dy: number, v: number) => L.hline(Math.min(foot(a, i), foot(b, i)), Math.max(foot(a, i), foot(b, i)), y + dy, v);
    [ly, ry].forEach((dy, i) => { for (const [y, a, b] of rows) span(y, a, b, i, dy, T.BASE); });
    L.shade({ right: 1, bottom: 0 });
    [ly, ry].forEach((dy, i) => {
      if (flats) {
        // ballet flats: low-cut over the toes, a tiny bow, a thin sole
        L.set(foot(17, i), SHOE_Y + 1 + dy, T.A).set(foot(16, i), SHOE_Y + 1 + dy, T.A).set(foot(14, i), SHOE_Y + 2 + dy, T.HI);
        span(SHOE_Y + 3, 13, 19, i, dy, T.B);
      } else {
        // chunky loafers over white socks: penny strap, a shine on the toe, a lug sole
        span(SHOE_Y, 16, 18, i, dy, T.W);
        span(SHOE_Y + 1, 15, 18, i, dy, T.SH);
        L.set(foot(17, i), SHOE_Y + 1 + dy, T.A).set(foot(14, i), SHOE_Y + 2 + dy, T.HI);
        span(SHOE_Y + 3, 13, 19, i, dy, T.B);
        L.set(foot(15, i), SHOE_Y + 3 + dy, T.DEEP).set(foot(18, i), SHOE_Y + 3 + dy, T.DEEP);
      }
    });
    return L;
  }
  if (p.kind === "flipflops") {
    // platform flip-flops: a thick foam sole under the bare foot and a thong strap between the toes
    const foot = (x: number, i: number) => (i ? SW - 1 - x : x);
    [ly, ry].forEach((dy, i) => {
      for (const yy of [SHOE_Y + 2, SHOE_Y + 3]) L.hline(Math.min(foot(13, i), foot(19, i)), Math.max(foot(13, i), foot(19, i)), yy + dy, T.BASE);
    });
    L.shade({ right: 1, bottom: 0 });
    [ly, ry].forEach((dy, i) => {
      L.hline(Math.min(foot(13, i), foot(19, i)), Math.max(foot(13, i), foot(19, i)), SHOE_Y + 3 + dy, T.B);
      L.set(foot(15, i), SHOE_Y + 1 + dy, T.A).set(foot(17, i), SHOE_Y + 1 + dy, T.A).set(foot(16, i), SHOE_Y + dy, T.A);
    });
    L.outline = null;
    return L;
  }
  for (const [x, dy] of [[13, ly], [21, ry]] as const) {
    // chunky sneakers with a thick sole
    L.rect(x + 1, SHOE_Y + dy, 5, 2, T.BASE);
    L.rect(x, SHOE_Y + 1 + dy, 7, 2, T.BASE);
    L.hline(x, x + 6, SHOE_Y + 3 + dy, T.B);
  }
  L.shade({ right: 1, bottom: 0 });
  for (const [x, dy] of [[13, ly], [21, ry]] as const)
    L.hline(x, x + 6, SHOE_Y + 3 + dy, T.B).set(x + 3, SHOE_Y + 1 + dy, T.A).set(x + 4, SHOE_Y + 1 + dy, T.A);
  return L;
}

// ---------------------------------------------------------------- hats, glasses, ears

function capRows(L: Layer, rows: number, extra: number, top: number, maxHw = 15) {
  for (let j = 0; j < rows; j++) {
    const hw = Math.min(maxHw, (HEAD_HW[j] ?? 13) + extra);
    L.hline(CX - hw, CX + hw - 1, top + j, T.BASE);
  }
}

function hatLayers(p: Part, f: Frame, hairStyle: string): { behind: Layer | null; front: Layer } {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#ff8fb0"]);
  const L = newLayer(pal);
  let behind: Layer | null = null;
  const puff = HAIR[hairStyle]?.puff ?? 1;
  switch (p.kind) {
    case "beanie":
    case "catbeanie": {
      // slouchy knit beanie pulled low over the forehead
      const dome = [5, 8, 10, 12, 13, 14, 14, 15, 15, 15, 15];
      dome.forEach((hw, j) => L.hline(CX - hw - (j > 2 ? 1 : 0), CX + hw - 1, HEAD_TOP - 5 + j, T.BASE));
      L.shade({ right: 3, bottom: 0 });
      const cuff = HEAD_TOP + 6;
      L.rect(CX - 16, cuff, 31, 4, T.BASE);
      L.hline(CX - 16, CX + 14, cuff, T.SH).hline(CX - 16, CX + 14, cuff + 3, T.SH);
      for (let x = CX - 15; x < CX + 15; x += 2) L.vline(x, cuff + 1, cuff + 2, T.SH);
      for (let x = CX - 12; x < CX + 12; x += 3) for (let y = HEAD_TOP - 3; y < cuff - 1; y++) if (L.get(x, y) === T.BASE) L.set(x, y, T.SH);
      L.hline(CX - 9, CX - 2, HEAD_TOP - 3, T.HI).hline(CX - 11, CX - 6, HEAD_TOP - 2, T.HI);
      if (p.kind === "catbeanie") {
        L.stamp(CX - 15, HEAD_TOP - 8, ["b.....", "bb....", "bbb...", "bbbb..", "bbbbb.", "bbbbbb", "bbbbbb"], { b: T.BASE });
        L.stamp(CX + 9, HEAD_TOP - 8, [".....b", "....bb", "...bbb", "..bbbb", ".bbbbb", "bbbbbb", "bbbbbb"], { b: T.BASE });
        L.vline(CX - 14, HEAD_TOP - 6, HEAD_TOP - 4, T.SH).vline(CX + 13, HEAD_TOP - 6, HEAD_TOP - 4, T.SH);
      } else if (p.accent && p.accent !== p.color) {
        L.ellipse(CX - 3, HEAD_TOP - 8, CX + 2, HEAD_TOP - 4, T.A); // pom
      }
      break;
    }
    case "cap": {
      // backwards cap; the brim sticks out the back
      capRows(L, 8, 1 + puff, HEAD_TOP - 2);
      L.shade({ right: 2, bottom: 1 });
      L.hline(CX - 14, CX + 13, HEAD_TOP + 5, T.SH);
      L.set(CX - 1, HEAD_TOP - 2, T.A).set(CX, HEAD_TOP - 2, T.A);
      if (!f.back) L.rect(CX - 4, HEAD_TOP + 2, 8, 3, T.SH).hline(CX - 3, CX + 2, HEAD_TOP + 3, T.A);
      else { behind = newLayer(pal); behind.rect(CX - 8, HEAD_TOP + 4, 16, 3, T.SH); }
      break;
    }
    case "bucket": {
      capRows(L, 9, puff, HEAD_TOP - 3, 14);
      L.spans([[HEAD_TOP + 6, CX - 16, CX + 15], [HEAD_TOP + 7, CX - 17, CX + 16], [HEAD_TOP + 8, CX - 17, CX + 16]]);
      L.shade({ right: 2, bottom: 1 });
      L.hline(CX - 14, CX + 13, HEAD_TOP + 5, T.A);
      break;
    }
    case "bandana": {
      capRows(L, 9, 1 + puff, HEAD_TOP - 2);
      L.shade({ right: 2, bottom: 1 });
      for (let j = 0; j < 8; j += 2) for (let x = CX - 12 + (j % 4); x < CX + 12; x += 4) if (L.get(x, HEAD_TOP - 1 + j)) L.set(x, HEAD_TOP - 1 + j, T.A);
      L.hline(CX - 14, CX + 13, HEAD_TOP + 6, T.DEEP);
      // knot tails hanging on the side
      L.stamp(CX - 19, HEAD_TOP + 5, ["bbbb", ".bbb", ".bbb", "..bb", "..bb", ".bb.", ".b.."], { b: T.BASE });
      break;
    }
    case "shark": {
      // full shark hood with a toothy rim around the face
      behind = newLayer(pal);
      behind.ellipse(CX - 17, HEAD_TOP - 7, CX + 16, CHIN_Y + 4, T.BASE);
      behind.stamp(CX + 15, HEAD_TOP + 9, ["bb..", "bbbb", "bbb.", "bb.."], { b: T.BASE }); // fin
      behind.stamp(CX - 2, HEAD_TOP - 11, ["..bb", ".bbb", "bbbb", "bbbb"], { b: T.BASE }); // dorsal fin
      behind.shade({ right: 3, bottom: 1 });
      if (f.back) break;
      // rim
      const ring = newLayer(pal);
      ring.ellipse(CX - 15, HEAD_TOP - 1, CX + 14, CHIN_Y + 4, T.BASE);
      const hole = newLayer(pal);
      hole.ellipse(CX - 12, HEAD_TOP + 2, CX + 11, CHIN_Y + 6, T.BASE);
      for (let i = 0; i < L.data.length; i++) if (ring.data[i] && !hole.data[i]) L.data[i] = T.BASE;
      L.shade({ right: 1, bottom: 0, hi: false });
      // red mouth lining and teeth along the top of the opening
      for (let x = CX - 11; x < CX + 11; x++) {
        let yy = HEAD_TOP + 1;
        while (yy < CHIN_Y && !(L.get(x, yy) && !L.get(x, yy + 1))) yy++;
        if (yy >= CHIN_Y - 6) continue;
        L.set(x, yy, T.B);
        if (x % 3 !== 0) L.set(x, yy + 1, T.W);
        if (x % 3 === 1) L.set(x, yy + 2, T.W);
      }
      L.ellipse(CX - 13, HEAD_TOP - 5, CX - 10, HEAD_TOP - 2, T.W).set(CX - 11, HEAD_TOP - 4, T.INK).set(CX - 12, HEAD_TOP - 4, T.INK);
      L.ellipse(CX + 9, HEAD_TOP - 5, CX + 12, HEAD_TOP - 2, T.W).set(CX + 10, HEAD_TOP - 4, T.INK).set(CX + 11, HEAD_TOP - 4, T.INK);
      for (const [x0, y0] of [[CX - 15, HEAD_TOP - 5], [CX + 10, HEAD_TOP - 5]]) for (let j = 0; j < 4; j++) if (!L.get(x0 + j, y0)) behind.set(x0 + j, y0, T.BASE);
      L.hline(CX - 15, CX - 14, CHIN_Y - 6, T.B).hline(CX + 13, CX + 14, CHIN_Y - 6, T.B);
      break;
    }
    case "durag": {
      // silky durag tied tight to the skull, tails down the back
      capRows(L, 10, puff > 1 ? 1 : 0, HEAD_TOP - 1, 14);
      L.shade({ right: 2, bottom: 1 });
      L.hline(CX - 13, CX + 12, HEAD_TOP + 8, T.DEEP);
      // satin shine and the center seam
      L.hline(CX - 9, CX - 4, HEAD_TOP, T.HI).hline(CX - 11, CX - 8, HEAD_TOP + 1, T.HI).set(CX - 12, HEAD_TOP + 3, T.HI);
      if (!f.back) L.vline(CX, HEAD_TOP - 1, HEAD_TOP + 7, T.SH);
      if (f.back) {
        L.vline(CX - 1, HEAD_TOP - 1, HEAD_TOP + 8, T.SH);
        L.rect(CX - 4, HEAD_TOP + 8, 7, 2, T.SH).set(CX - 1, HEAD_TOP + 9, T.DEEP); // knot
        const tails = newLayer(pal);
        tails.poly([[CX - 4, HEAD_TOP + 10], [CX, HEAD_TOP + 10], [CX - 2, CHIN_Y + 8], [CX - 5, CHIN_Y + 7]]);
        tails.poly([[CX, HEAD_TOP + 10], [CX + 4, HEAD_TOP + 10], [CX + 5, CHIN_Y + 6], [CX + 2, CHIN_Y + 7]]);
        tails.shade({ right: 1, bottom: 0, hi: false });
        for (let i = 0; i < L.data.length; i++) if (tails.data[i] && !L.data[i]) L.data[i] = tails.data[i];
      }
      break;
    }
    case "fitted": {
      // forward-facing fitted cap; flat brim over the eyes, one small mark on the front panel
      capRows(L, 8, 1 + puff, HEAD_TOP - 2);
      L.shade({ right: 2, bottom: 1 });
      L.set(CX - 1, HEAD_TOP - 2, T.A).set(CX, HEAD_TOP - 2, T.A);
      if (!f.back) {
        L.vline(CX - 7, HEAD_TOP, HEAD_TOP + 5, T.SH).vline(CX + 6, HEAD_TOP, HEAD_TOP + 5, T.SH);
        L.spans([[HEAD_TOP + 6, CX - 15, CX + 14], [HEAD_TOP + 7, CX - 15, CX + 14], [HEAD_TOP + 8, CX - 14, CX + 13]], T.SH);
        L.hline(CX - 13, CX + 12, HEAD_TOP + 8, T.DEEP).hline(CX - 12, CX + 4, HEAD_TOP + 6, T.BASE);
        L.mark(CX - 3, HEAD_TOP + 1, ["b...b", ".b.b.", "..b..", ".b.b.", "b...b"], { b: T.A });
      } else {
        L.hline(CX - 14, CX + 13, HEAD_TOP + 5, T.SH);
        L.rect(CX - 3, HEAD_TOP + 3, 6, 2, T.DEEP).hline(CX - 3, CX + 2, HEAD_TOP + 5, T.A); // snapback strap
      }
      break;
    }
    case "dadcap": {
      // worn-in curved-brim cap, plain front panel, metal buckle on the back strap
      capRows(L, 8, 1 + puff, HEAD_TOP - 2);
      L.shade({ right: 2, bottom: 1 });
      L.set(CX - 1, HEAD_TOP - 2, T.A).set(CX, HEAD_TOP - 2, T.A);
      if (!f.back) {
        L.vline(CX - 7, HEAD_TOP, HEAD_TOP + 5, T.SH).vline(CX + 6, HEAD_TOP, HEAD_TOP + 5, T.SH).vline(CX - 1, HEAD_TOP - 1, HEAD_TOP + 5, T.SH);
        L.spans([[HEAD_TOP + 6, CX - 14, CX + 13], [HEAD_TOP + 7, CX - 12, CX + 11], [HEAD_TOP + 8, CX - 9, CX + 8]], T.SH);
        L.hline(CX - 9, CX + 8, HEAD_TOP + 8, T.DEEP).hline(CX - 11, CX + 3, HEAD_TOP + 6, T.BASE);
      } else {
        L.hline(CX - 14, CX + 13, HEAD_TOP + 5, T.SH);
        L.rect(CX - 3, HEAD_TOP + 3, 6, 2, T.DEEP).hline(CX - 3, CX + 2, HEAD_TOP + 5, T.SH).set(CX + 1, HEAD_TOP + 5, T.A);
      }
      break;
    }
    case "trucker": {
      // tall foam-front trucker hat worn forward: a rounded front panel in the base color, mesh sides,
      // back and brim in accent, a little heart in accent2 on the front
      pal[T.C] = mix(hex(p.accent ?? p.color), [20, 12, 24], 0.35); // the holes in the mesh
      capRows(L, 9, 1 + puff, HEAD_TOP - 3);
      L.shade({ right: 2, bottom: 1 });
      const panel = (x: number, yy: number) => !f.back && Math.abs(x - CX + 0.5) < 12.5 - Math.max(0, HEAD_TOP - yy) * 2;
      for (let yy = HEAD_TOP - 3; yy < HEAD_TOP + 6; yy++) for (let x = 0; x < SW; x++) {
        if (!L.get(x, yy) || panel(x, yy)) continue;
        L.set(x, yy, (x + yy) % 2 ? T.A : T.C);
      }
      L.hline(CX - 1, CX, HEAD_TOP - 3, T.SH);
      if (!f.back) {
        L.vline(CX - 1, HEAD_TOP - 2, HEAD_TOP - 1, T.SH);
        L.mark(CX - 3, HEAD_TOP, ["bb.bb", "bbbbb", ".bbb.", "..b.."], { b: T.B }); // a little heart on the foam front
        L.spans([[HEAD_TOP + 6, CX - 14, CX + 13], [HEAD_TOP + 7, CX - 12, CX + 11], [HEAD_TOP + 8, CX - 9, CX + 8]], T.A);
        L.hline(CX - 9, CX + 8, HEAD_TOP + 8, T.C);
      } else {
        L.rect(CX - 3, HEAD_TOP + 3, 6, 2, T.DEEP).hline(CX - 3, CX + 2, HEAD_TOP + 5, T.SH);
      }
      break;
    }
    case "clips": {
      // a scatter of little butterfly clips pinned into the hair, alternating colors
      L.outline = null;
      const spots: [number, number][] = f.back
        ? [[CX - 11, HEAD_TOP + 4], [CX - 4, HEAD_TOP + 1], [CX + 3, HEAD_TOP + 3], [CX + 8, HEAD_TOP + 7]]
        : [[CX - 12, HEAD_TOP + 3], [CX - 5, HEAD_TOP], [CX + 2, HEAD_TOP + 1], [CX + 8, HEAD_TOP + 4]];
      spots.forEach(([x, yy], i) => {
        const c = [T.BASE, T.A, T.B][i % 3];
        L.stamp(x, yy, ["c.d.c", "ccdcc", ".cdc."], { c, d: T.INK });
      });
      break;
    }
    case "balaclava": {
      // knit ski mask over the whole head, bunched at the neck, one wide opening for the eyes
      L.hline(CX - 6, CX + 5, HEAD_TOP - 1, T.BASE);
      HEAD_HW.forEach((hw, j) => L.hline(CX - hw - 1, CX + hw, HEAD_TOP + j, T.BASE));
      L.rect(CX - 5, CHIN_Y + 1, 10, 2, T.BASE);
      L.shade({ right: 2, bottom: 1 });
      for (let x = CX - 12; x < CX + 13; x += 3) for (let yy = HEAD_TOP; yy < CHIN_Y; yy++) if (L.get(x, yy) === T.BASE) L.set(x, yy, T.SH);
      L.hline(CX - 5, CX + 4, CHIN_Y + 1, T.SH);
      if (!f.back) {
        L.erase(CX - 11, EYE_Y - 3, 22, 10);
        for (const [cx, cy] of [[CX - 11, EYE_Y - 3], [CX + 10, EYE_Y - 3], [CX - 11, EYE_Y + 6], [CX + 10, EYE_Y + 6]]) L.set(cx, cy, T.BASE);
        L.hline(CX - 10, CX + 9, EYE_Y - 4, T.DEEP); // rolled edge over the brow
      }
      break;
    }
    case "headband":
      L.hline(CX - 14, CX + 13, HEAD_TOP + 4, T.BASE).hline(CX - 14, CX + 13, HEAD_TOP + 5, T.BASE);
      L.ellipse(CX + 6, HEAD_TOP, CX + 10, HEAD_TOP + 4, T.A);
      break;
  }
  return { behind, front: L };
}

function faceLayer(p: Part): Layer | null {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#ffffff"]);
  const L = newLayer(pal, null);
  const y = EYE_Y;
  switch (p.kind) {
    case "shades": // wraparound black shades
      L.hline(8, 31, y, T.DEEP);
      L.rect(9, y + 1, 9, 4, T.BASE).rect(22, y + 1, 9, 4, T.BASE).hline(18, 21, y + 1, T.DEEP);
      L.hline(10, 12, y + 2, T.A).hline(23, 25, y + 2, T.A);
      L.hline(10, 17, y + 4, T.SH).hline(22, 29, y + 4, T.SH);
      L.outline = [18, 14, 22];
      break;
    case "tinted": // rimless rectangular tinted glasses
      L.rect(9, y + 1, 9, 5, T.BASE).rect(22, y + 1, 9, 5, T.BASE);
      L.hline(18, 21, y + 2, T.DEEP).hline(10, 11, y + 2, T.A).hline(23, 24, y + 2, T.A);
      L.hline(8, 8, y + 2, T.DEEP).hline(31, 31, y + 2, T.DEEP);
      L.outline = mix(hex(p.color), [20, 10, 0], 0.7);
      for (let i = 0; i < L.data.length; i++) if (L.data[i] === T.BASE && (i % SW) % 2) L.data[i] = T.HI;
      break;
    case "visor": // y2k shield visor
      L.spans([[y, 9, 30], [y + 1, 8, 31], [y + 2, 8, 31], [y + 3, 8, 31], [y + 4, 9, 30]]);
      L.shade({ right: 3, bottom: 1 });
      L.hline(10, 17, y + 1, T.A).hline(10, 13, y + 2, T.A);
      L.outline = [20, 30, 60];
      break;
    case "shield": // slim wraparound y2k shield, one lens with a sheen
      L.spans([[y + 2, 8, 31], [y + 3, 8, 31], [y + 4, 9, 30]]);
      L.shade({ right: 3, bottom: 1 });
      L.hline(10, 15, y + 2, T.A).hline(24, 26, y + 2, T.A).hline(19, 20, y + 4, T.DEEP);
      L.outline = mix(hex(p.color), [10, 10, 20], 0.75);
      break;
    case "slim": // narrow 90s rectangle shades
      L.rect(10, y + 2, 7, 2, T.BASE).rect(23, y + 2, 7, 2, T.BASE);
      L.hline(17, 22, y + 2, T.DEEP).hline(8, 9, y + 2, T.DEEP).hline(30, 31, y + 2, T.DEEP);
      L.hline(11, 12, y + 2, T.A).hline(24, 25, y + 2, T.A);
      L.outline = mix(hex(p.color), [10, 6, 12], 0.8);
      break;
    case "ovals": // tiny oval shades on a thin wire bridge
      L.ellipse(10, y + 1, 16, y + 4, T.BASE).ellipse(23, y + 1, 29, y + 4, T.BASE);
      L.hline(17, 22, y + 2, T.DEEP).set(9, y + 2, T.DEEP).set(30, y + 2, T.DEEP);
      L.hline(11, 12, y + 2, T.A).hline(24, 25, y + 2, T.A);
      L.outline = mix(hex(p.color), [10, 6, 12], 0.8);
      break;
    case "aviator": // tinted aviators: teardrop lenses fading lighter toward the bottom, a double bridge
      for (const flip of [false, true]) L.stamp(flip ? 22 : 9, y, ["ssssssss", "bbbbbbbb", "abbbbbbb", ".hhhhhh.", "..hhhh.."], { s: T.SH, b: T.BASE, a: T.A, h: T.HI }, flip);
      L.hline(17, 22, y, T.DEEP).hline(18, 21, y + 1, T.DEEP).set(8, y, T.DEEP).set(31, y, T.DEEP);
      L.outline = mix(hex(p.accent2 ?? p.color), [10, 6, 12], 0.5);
      break;
    case "star": // star party glasses
      for (const cx of [13, 26]) L.stamp(cx - 3, y - 1, ["...b...", "..bbb..", "bbbbbbb", ".bbbbb.", ".bb.bb."], { b: T.BASE });
      L.hline(17, 22, y + 1, T.BASE);
      L.outline = mix(hex(p.color), [30, 0, 30], 0.7);
      break;
  }
  return L;
}

function earsLayer(p: Part, f: Frame): Layer | null {
  const pal = ramp(p.color, [p.accent ?? "#ffffff", p.accent2 ?? "#ff4f9a"]);
  const L = newLayer(pal);
  const cy = EYE_Y + 1;
  if (p.kind === "headphones" || p.kind === "bunnyphones") {
    if (p.kind === "headphones") {
      // band over the top of the head
      for (let y = HEAD_TOP - 5; y <= cy - 3; y++) {
        const j = y - (HEAD_TOP - 5);
        const hw = Math.min(16, 8 + j * 2);
        if (j < 2) L.hline(CX - hw, CX + hw - 1, y, T.BASE);
        else { L.hline(CX - hw, CX - hw + 1, y, T.BASE); L.hline(CX + hw - 2, CX + hw - 1, y, T.BASE); }
      }
    } else {
      L.stamp(CX - 20, HEAD_TOP - 3, ["bb....", "bbb...", ".bbb..", "..bbb.", "...bbb", "....bb"], { b: T.BASE });
      L.stamp(CX + 14, HEAD_TOP - 3, ["....bb", "...bbb", "..bbb.", ".bbb..", "bbb...", "bb...."], { b: T.BASE });
    }
    // big round ear cups
    L.ellipse(CX - 19, cy - 5, CX - 13, cy + 5, T.BASE);
    L.ellipse(CX + 12, cy - 5, CX + 18, cy + 5, T.BASE);
    L.shade({ right: 2, bottom: 1 });
    L.ellipse(CX - 18, cy - 3, CX - 16, cy + 3, T.A).ellipse(CX + 15, cy - 3, CX + 17, cy + 3, T.A);
    L.vline(CX - 17, cy - 2, cy + 2, T.HI).vline(CX + 16, cy - 2, cy + 2, T.HI);
    if (p.kind === "bunnyphones") L.set(CX - 17, cy, T.B).set(CX + 16, cy, T.B);
  } else if (p.kind === "sling") {
    // crossbody bag: strap from the right shoulder to a pouch on the left hip
    for (let i = 0; i <= 10; i++) {
      const xx = 24 - i, yy = SHOULDER_Y - 1 + i;
      L.set(xx, yy, T.BASE).set(xx - 1, yy, T.SH);
    }
    if (f.back) return L;
    L.rect(11, WAIST_Y - 4, 8, 5, T.BASE);
    L.shade({ right: 1, bottom: 1 });
    L.hline(11, 18, WAIST_Y - 3, T.SH).hline(12, 17, WAIST_Y - 4, T.HI);
    L.rect(14, WAIST_Y - 3, 2, 2, T.A); // buckle
  } else if (p.kind === "hoops") {
    // big thin hoops hanging from both ears
    L.outline = null;
    for (const x of [CX - 16, CX + 12]) {
      L.stamp(x, EYE_Y + 2, [".bb.", "b..b", "b..b", "b..b", ".bb."], { b: T.BASE });
      L.set(x, EYE_Y + 3, T.A).set(x + 3, EYE_Y + 5, T.DEEP).set(x + 2, EYE_Y + 6, T.DEEP);
    }
  } else if (p.kind === "baguette") {
    // little shoulder bag tucked under the arm on a short strap
    const bx = f.back ? 26 : 8, sx = f.back ? 24 : 15;
    for (let i = 0; i <= 5; i++) L.set(f.back ? sx + Math.round(i * 0.5) : sx - Math.round(i * 0.5), SHOULDER_Y - 1 + i, T.SH);
    L.rect(bx, SHOULDER_Y + 5, 6, 4, T.BASE);
    L.shade({ right: 1, bottom: 1 });
    L.hline(bx, bx + 5, SHOULDER_Y + 5, T.HI).hline(bx, bx + 5, SHOULDER_Y + 6, T.SH);
    if (!f.back) L.set(bx + 2, SHOULDER_Y + 7, T.A).set(bx + 3, SHOULDER_Y + 7, T.A); // clasp
  } else if (p.kind === "choker") {
    // thin choker: rhinestones all the way round, or velvet with a little butterfly charm (accent2)
    L.outline = null;
    for (let x = CX - 4; x <= CX + 3; x++) L.set(x, CHIN_Y + 1, x % 2 ? T.BASE : T.A);
    if (f.back) return L;
    if (p.accent2) L.stamp(CX - 2, CHIN_Y + 2, ["b..b", "bbbb", ".bb."], { b: T.B }).set(CX - 1, CHIN_Y + 2, T.DEEP).set(CX, CHIN_Y + 2, T.DEEP);
    else L.set(CX - 1, CHIN_Y + 2, T.A).set(CX, CHIN_Y + 2, T.A);
  } else if (p.kind === "collar") {
    // studded leather choker with little spikes hanging off it
    L.hline(CX - 4, CX + 3, CHIN_Y + 1, T.BASE).hline(CX - 4, CX + 3, CHIN_Y + 2, T.SH);
    if (f.back) return L;
    L.set(CX - 3, CHIN_Y + 1, T.A).set(CX + 2, CHIN_Y + 1, T.A); // studs
    L.hline(CX - 1, CX, CHIN_Y + 2, T.A).hline(CX - 1, CX, CHIN_Y + 3, T.A); // ring hanging in front
  } else if (p.kind === "padlock") {
    // heavy curb chain with a padlock hanging in the middle
    if (f.back) return null;
    for (let x = 15; x <= 24; x++) {
      const d = Math.abs(x - 19.5);
      const yy = SHOULDER_Y + (d < 2 ? 2 : d < 4 ? 1 : 0);
      L.set(x, yy, x % 2 ? T.BASE : T.HI).set(x, yy + 1, x % 2 ? T.SH : T.BASE);
    }
    L.hline(19, 20, SHOULDER_Y + 4, T.DEEP).rect(18, SHOULDER_Y + 5, 4, 3, T.A).set(20, SHOULDER_Y + 6, T.DEEP);
  } else if (p.kind === "chain") {
    if (f.back) return null;
    L.outline = null;
    for (let x = 16; x <= 23; x++) {
      const d = Math.abs(x - 19.5);
      L.set(x, SHOULDER_Y + (d < 2 ? 2 : d < 3.5 ? 1 : 0), x % 2 ? T.BASE : T.HI);
    }
    L.rect(19, SHOULDER_Y + 3, 2, 2, T.A);
  }
  return L;
}

// ---------------------------------------------------------------- compose

/** Bottoms long enough to pool over the shoes instead of tucking in. */
const POOLED = new Set(["flares", "wideleg", "trousers", "lowrise", "velourpants"]);

/** `flip` mirrors the sprite for left-facing directions; prints drawn with `mark` keep reading the right way. */
export function buildChibi(look: ChibiLook, f: Frame, flip = false): Bitmap {
  const back = f.back;
  const hat = look.hat ? hatLayers(look.hat, f, look.hairStyle) : null;
  const hidesHair = look.hat?.kind === "shark" || look.hat?.kind === "durag" || look.hat?.kind === "balaclava";
  const top = topLayers(look.top, f);
  const [la, ra] = arms(f);
  const armBits = (a: Arm) => [armSkin(look.skin, a), top.sleeveFor(a)];
  const lowArms = [la, ra].filter((a) => !a.up).flatMap(armBits);
  const highArms = [la, ra].filter((a) => a.up).flatMap(armBits);
  const hatted = !!look.hat && ["beanie", "catbeanie", "cap", "bucket", "durag", "fitted", "dadcap", "trucker"].includes(look.hat.kind);
  const hair = hidesHair ? null : hairFront(look.hairColor, look.hairStyle, back, hatted);
  const chest = look.ears?.kind === "chain" || look.ears?.kind === "padlock";
  // flares and wide legs are worn over the shoes; everything else tucks into tall shoes or stops at the ankle
  const toes = look.shoes.kind === "flats", bare = look.shoes.kind === "flipflops";
  const skin = BARE.has(look.top.kind) || LOW_RISE.has(look.bottom.kind) ? torso(look.skin) : null;
  const lower = POOLED.has(look.bottom.kind)
    ? [skin, legs(look.skin, f, toes, bare), shoesLayer(look.shoes, f), bottomLayer(look.bottom, f)]
    : [skin, legs(look.skin, f, toes, bare), bottomLayer(look.bottom, f, shoeTop(look.shoes.kind)), shoesLayer(look.shoes, f)];
  const bag = look.ears?.kind === "sling" || look.ears?.kind === "baguette";
  let layers: (Layer | null)[] = back
    ? [
        ...lower,
        ...lowArms, top.body, bag ? earsLayer(look.ears!, f) : null, ...highArms,
        head(look.skin, look.eyeColor, null, true),
        hair, hat?.behind ?? null, hat?.front ?? null,
        look.ears && !bag ? earsLayer(look.ears, f) : null,
      ]
    : [
        hidesHair ? null : hairBack(look.hairColor, look.hairStyle),
        hat?.behind ?? null,
        ...lower,
        top.body, chest ? earsLayer(look.ears!, f) : null, ...lowArms,
        bag ? earsLayer(look.ears!, f) : null,
        head(look.skin, look.eyeColor, hidesHair ? null : look.hairStyle, false),
        look.face ? faceLayer(look.face) : null,
        hair, hat?.front ?? null,
        look.ears && !chest && !bag ? earsLayer(look.ears, f) : null,
        ...highArms,
      ];
  if (flip) layers = layers.map((L) => L && L.mirrored());
  if (f.bob) for (const L of layers) if (L) shiftY(L, f.bob);
  return composite(SW, SH, layers);
}

function shiftY(L: Layer, dy: number) {
  const d = new Uint8Array(L.data.length);
  for (let y = 0; y < L.h; y++) {
    const sy = y - dy;
    if (sy < 0 || sy >= L.h) continue;
    d.set(L.data.subarray(sy * L.w, sy * L.w + L.w), y * L.w);
  }
  L.data = d;
}

/** Just one cosmetic, with no body under it, cropped to its own pixels (for shop and closet tiles). */
export function buildItem(slot: string, p: Part): Bitmap | null {
  const f: Frame = { back: false, step: 0, arms: "down" };
  let layers: (Layer | null)[] = [];
  if (slot === "top") {
    const top = topLayers(p, f);
    layers = [...arms(f).map((a) => top.sleeveFor(a)), top.body];
  } else if (slot === "bottom") layers = [bottomLayer(p, f)];
  else if (slot === "shoes") layers = [shoesLayer(p, f)];
  else if (slot === "hat") { const h = hatLayers(p, f, "fluffy"); layers = [h.behind, h.front]; }
  else if (slot === "glasses") layers = [faceLayer(p)];
  else if (slot === "acc") layers = [earsLayer(p, f)];
  const b = composite(SW, SH, layers);
  if (p.kind === "hoops") return crop(b, { x: CX - 16, y: EYE_Y + 2, w: 4, h: 5 }); // one hoop reads better than a far-apart pair
  const r = bounds(b);
  return r ? crop(b, r) : null;
}

/**
 * A hairstyle on a mannequin head stand, for shop and closet tiles. Bald is just the head. Every style
 * is cropped to the same box, so heads come out the same size whether the hair is short or long.
 */
export function buildHairItem(styleId: string, color: string): Bitmap | null {
  const L = newLayer(ramp("#c8c8d2"), hex("#5a5a68"));
  HEAD_HW.forEach((hw, j) => L.hline(CX - hw, CX + hw - 1, HEAD_TOP + j, T.BASE));
  L.shade({ right: 2, bottom: 1 });
  const st = HAIR[styleId];
  if (st) for (let x = CX - 12; x < CX + 12; x++) L.set(x, edgeAt(st.bangs, x) + 1, T.SH);
  // neck, pole and round base
  const stand = newLayer(ramp("#8a8a98"), hex("#3a3a46"));
  stand.rect(CX - 3, CHIN_Y + 1, 6, 3, T.BASE).rect(CX - 1, CHIN_Y + 4, 2, FOOT_Y - CHIN_Y - 6, T.BASE);
  stand.ellipse(CX - 8, FOOT_Y - 3, CX + 7, FOOT_Y, T.BASE);
  stand.shade({ right: 1, bottom: 1 });
  const b = composite(SW, SH, [hairBack(color, styleId), stand, L, hairFront(color, styleId, false)]);
  return crop(b, { x: 0, y: 0, w: SW, h: SH });
}

export { flipX };
